import React, { useState, useEffect, useRef } from 'react';
import { 
  ChevronRight, ChevronDown, Search, Upload, RefreshCw, 
  Trash2, Eye, Download, Copy, CheckCircle,
  FolderOpen, X, Folder, Grid, List, File, Plus, Users, MoreVertical, FolderPlus, Filter, RotateCcw,
  FileText, FileSpreadsheet, FileImage, FileVideo, FileAudio, FileArchive, FileCode, Move, AlertCircle, Check, Edit2
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import PDFViewer from '../components/PDFViewer';
import OfficeViewer from '../components/OfficeViewer';

const ClientRecords = () => {
  const { token } = useAuth();
  
  // Notification state
  const [notification, setNotification] = useState({ show: false, message: '', type: 'success' });
  
  const [clientFolders, setClientFolders] = useState([]);
  const [selectedPath, setSelectedPath] = useState([]); // Array of path segments
  const [recordFiles, setRecordFiles] = useState([]);
  const [letterCounts, setLetterCounts] = useState({});
  const [loadedLetters, setLoadedLetters] = useState({});
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [copiedUrl, setCopiedUrl] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [previewData, setPreviewData] = useState({ url: '', name: '', type: '' });
  const [expandedFolders, setExpandedFolders] = useState(new Set(['root'])); // Track expanded folders in tree
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'
  const [filterMode, setFilterMode] = useState('both'); // 'files', 'folders', 'both'
  const [showFilterDropdown, setShowFilterDropdown] = useState(false);
  const [leftPanelWidth, setLeftPanelWidth] = useState(300); // Default width in pixels
  const [isResizing, setIsResizing] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [showAddCustomerModal, setShowAddCustomerModal] = useState(false);
  const [contextMenu, setContextMenu] = useState({ show: false, x: 0, y: 0, file: null });
  const [showCreateFolderModal, setShowCreateFolderModal] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [showRenameFolderModal, setShowRenameFolderModal] = useState(false);
  const [folderToRename, setFolderToRename] = useState(null);
  const [renameFolderValue, setRenameFolderValue] = useState('');
  const [showRenameFileModal, setShowRenameFileModal] = useState(false);
  const [fileToRename, setFileToRename] = useState(null);
  const [renameFileValue, setRenameFileValue] = useState('');
  const [showMoveFileModal, setShowMoveFileModal] = useState(false);
  const [fileToMove, setFileToMove] = useState(null);
  const [moveDestinationPath, setMoveDestinationPath] = useState([]);
  const [moveDestinationKey, setMoveDestinationKey] = useState('');
  const [availableFolders, setAvailableFolders] = useState([]);
  const [showRecycleBin, setShowRecycleBin] = useState(false);
  const [recycleBinItems, setRecycleBinItems] = useState([]);
  const [recycleBinBrowsePath, setRecycleBinBrowsePath] = useState([]); // For browsing into folders in recycle bin
  const [recycleBinBrowseFolder, setRecycleBinBrowseFolder] = useState(null); // Current deleted folder being browsed
  const [showRestoreConfirmModal, setShowRestoreConfirmModal] = useState(false);
  const [itemToRestore, setItemToRestore] = useState(null);
  const [isGlobalRestore, setIsGlobalRestore] = useState(false);
  const [showGlobalRecycleBin, setShowGlobalRecycleBin] = useState(false);
  const [globalRecycleBinItems, setGlobalRecycleBinItems] = useState([]);
  const [globalRecycleBinBrowseFolder, setGlobalRecycleBinBrowseFolder] = useState(null); // For browsing global recycle bin folders
  const [globalRecycleBinBrowseClient, setGlobalRecycleBinBrowseClient] = useState(null); // Client ID when browsing
  const [globalRecycleBinBrowsePath, setGlobalRecycleBinBrowsePath] = useState([]); // For nested navigation in global
  // Removed expandedVersions state since we're not using version expansion anymore
  const [showVersionsModal, setShowVersionsModal] = useState(false);
  const [selectedFileVersions, setSelectedFileVersions] = useState([]);
  const [selectedFileName, setSelectedFileName] = useState('');
  const [customerFormData, setCustomerFormData] = useState({
    first_name: '',
    last_name: '',
    pets: [{ name: '' }],
    phone: '',
    email: '',
    sms_opt_in: true,
    email_subscribed: true
  });
  
  // Folder tree navigation states
  const [expandedFoldersTree, setExpandedFoldersTree] = useState(new Set()); // Track expanded folders in tree view
  const [folderTreeCache, setFolderTreeCache] = useState({}); // Cache subfolders for each path
  const [loadingFolders, setLoadingFolders] = useState(new Set()); // Track which folders are being loaded
  const [refreshingTree, setRefreshingTree] = useState(false); // Track if tree is being refreshed
  
  // Tree context menu states
  const [treeContextMenu, setTreeContextMenu] = useState({ show: false, x: 0, y: 0, folder: null, clientId: null, folderPath: '' });
  const [clientContextMenu, setClientContextMenu] = useState({ show: false, x: 0, y: 0, client: null });
  const [showCreateSubfolderModal, setShowCreateSubfolderModal] = useState(false);
  const [newSubfolderName, setNewSubfolderName] = useState('');
  const [createSubfolderParent, setCreateSubfolderParent] = useState({ clientId: null, folderPath: '', clientName: '' });
  
  const resizeRef = useRef(null);
  
  const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || (window.location.origin.includes("localhost") ? "http://localhost:8000" : window.location.origin + "/pandvah-backend");

  // Fetch client folders
  const fetchClientFolders = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/records/clients`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setClientFolders(data.clients || []);
      } else {
        console.error('Failed to fetch client folders');
        setClientFolders([]);
      }
    } catch (error) {
      console.error('Error fetching client folders:', error);
      setClientFolders([]);
    } finally {
      setLoading(false);
    }
  };

  // Show notification
  const showNotification = (message, type = 'success') => {
    setNotification({ show: true, message, type });
    setTimeout(() => {
      setNotification({ show: false, message: '', type: 'success' });
    }, 3000); // Auto-hide after 3 seconds
  };


  // Fetch files for a specific client
  const fetchClientFiles = async (clientId, subPath = '') => {
    setLoading(true);
    try {
      const url = subPath 
        ? `${API_BASE_URL}/api/records/clients/${clientId}/files?sub_path=${encodeURIComponent(subPath)}`
        : `${API_BASE_URL}/api/records/clients/${clientId}/files`;
      
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        // Combine folders and files, folders first
        const folders = data.folders || [];
        const files = data.files || [];
        setRecordFiles([...folders, ...files]);
      } else {
        console.error('Failed to fetch client files');
        setRecordFiles([]);
      }
    } catch (error) {
      console.error('Error fetching client files:', error);
      setRecordFiles([]);
    } finally {
      setLoading(false);
    }
  };

  // Upload file to client folder
  const uploadClientFile = async (clientId, file) => {
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await fetch(`${API_BASE_URL}/api/records/clients/${clientId}/files`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      if (response.ok) {
        await fetchClientFiles(clientId);
        return true;
      } else {
        console.error('Failed to upload client file');
        return false;
      }
    } catch (error) {
      console.error('Error uploading client file:', error);
      return false;
    }
  };

  // Delete file from client folder
  const deleteClientFile = async (clientId, fileKey) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/records/clients/${clientId}/files/${encodeURIComponent(fileKey)}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        await fetchClientFiles(clientId);
        return true;
      } else {
        console.error('Failed to delete client file');
        return false;
      }
    } catch (error) {
      console.error('Error deleting client file:', error);
      return false;
    }
  };

  // Load client folders (lazy loading - only counts initially)
  const loadClientFolders = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/records/clients`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setLetterCounts(data.letter_counts || {});
      } else {
        console.error('Failed to fetch client folder counts');
      }
    } catch (error) {
      console.error('Error fetching client folder counts:', error);
    }
  };

  // Load clients for a specific letter
  const loadClientsForLetter = async (letter) => {
    if (loadedLetters[letter]) {
      // Already loaded
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/api/records/clients/letter/${letter}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        // Add these clients to the clientFolders array
        setClientFolders(prev => {
          // Filter out any existing clients with same IDs to prevent duplicates
          const existingIds = new Set(prev.map(c => c.id));
          const newClients = data.clients.filter(c => !existingIds.has(c.id));
          return [...prev, ...newClients];
        });
        
        // Mark this letter as loaded
        setLoadedLetters(prev => ({ ...prev, [letter]: true }));
      } else {
        console.error('Failed to fetch clients for letter:', letter);
      }
    } catch (error) {
      console.error('Error fetching clients for letter:', error);
    }
  };
  // Load client folders on mount
  useEffect(() => {
    loadClientFolders();
  }, []);

  // Close context menu on outside click
  useEffect(() => {
    const handleClick = () => setContextMenu({ show: false, x: 0, y: 0, file: null });
    if (contextMenu.show) {
      document.addEventListener('click', handleClick);
    }
    return () => document.removeEventListener('click', handleClick);
  }, [contextMenu.show]);

  // Handle panel resizing
  useEffect(() => {
    const handleMouseMove = (e) => {
      if (isResizing) {
        const newWidth = e.clientX;
        if (newWidth >= 200 && newWidth <= 600) { // Min 200px, Max 600px
          setLeftPanelWidth(newWidth);
        }
      }
    };

    const handleMouseUp = () => {
      setIsResizing(false);
    };

    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isResizing]);

  // Organize clients by first letter of last name
  const organizeClientsByLetter = () => {
    const organized = {};
    
    // Initialize with all letters that have counts
    Object.keys(letterCounts).forEach(letter => {
      organized[letter] = [];
    });
    
    // Add loaded clients
    clientFolders.forEach(client => {
      // Extract last name
      let lastName = '';
      if (client.name.includes(' ')) {
        const parts = client.name.split(' ');
        lastName = parts[parts.length - 1]; // Last word is last name
      } else {
        lastName = client.name;
      }
      
      const firstLetter = lastName.charAt(0).toUpperCase();
      const letter = firstLetter >= 'A' && firstLetter <= 'Z' ? firstLetter : '#';
      
      if (!organized[letter]) {
        organized[letter] = [];
      }
      organized[letter].push(client);
    });
    
    return organized;
  };

  // Toggle folder expansion in tree
  const toggleFolder = (folderId) => {
    setExpandedFolders(prev => {
      const newExpanded = new Set(prev);
      if (newExpanded.has(folderId)) {
        newExpanded.delete(folderId);
      } else {
        newExpanded.add(folderId);
        
        // If it's a letter folder and not loaded yet, load the clients
        if (folderId.startsWith('letter-')) {
          const letter = folderId.replace('letter-', '');
          loadClientsForLetter(letter);
        }
      }
      return newExpanded;
    });
  };

  // Select a client/folder
  const selectClient = (client) => {
    setSelectedPath([client.name]);
    fetchClientFiles(client.id);
  };

  // Navigate into a subfolder
  const navigateIntoFolder = (folderName) => {
    const newPath = [...selectedPath, folderName];
    setSelectedPath(newPath);
    setRecordFiles([]);
    
    // Fetch files from subfolder
    const clientId = clientFolders.find(c => c.name === selectedPath[0])?.id;
    if (clientId) {
      const subPath = newPath.slice(1).join('/');
      fetchClientFiles(clientId, subPath);
    }
  };

  // Navigate back to a specific breadcrumb level
  const navigateToBreadcrumb = (index) => {
    const newPath = selectedPath.slice(0, index + 1);
    setSelectedPath(newPath);
    setRecordFiles([]);
    
    const clientId = clientFolders.find(c => c.name === newPath[0])?.id;
    if (clientId) {
      if (newPath.length === 1) {
        // Back to root - fetch client files
        fetchClientFiles(clientId);
      } else {
        // Navigate to specific subfolder
        const subPath = newPath.slice(1).join('/');
        fetchClientFiles(clientId, subPath);
      }
    }
  };

  // Get breadcrumb from selected path
  const getBreadcrumb = () => {
    if (selectedPath.length === 0) return 'Select a client';
    return selectedPath.join(' / ');
  };

  // Fetch subfolders for tree navigation (lazy loading)
  const fetchSubfoldersForTree = async (clientId, folderPath) => {
    const cacheKey = `${clientId}/${folderPath}`;
    
    // Return cached data if available
    if (folderTreeCache[cacheKey]) {
      return folderTreeCache[cacheKey];
    }
    
    // Mark as loading
    setLoadingFolders(prev => new Set([...prev, cacheKey]));
    
    try {
      const url = folderPath 
        ? `${API_BASE_URL}/api/records/clients/${clientId}/files?sub_path=${encodeURIComponent(folderPath)}`
        : `${API_BASE_URL}/api/records/clients/${clientId}/files`;
      
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        const folders = data.folders || [];
        
        // Cache the subfolders
        setFolderTreeCache(prev => ({
          ...prev,
          [cacheKey]: folders
        }));
        
        return folders;
      }
    } catch (error) {
      console.error('Error fetching subfolders for tree:', error);
    } finally {
      // Remove loading state
      setLoadingFolders(prev => {
        const newSet = new Set(prev);
        newSet.delete(cacheKey);
        return newSet;
      });
    }
    
    return [];
  };

  // Toggle folder expansion in tree
  const toggleFolderTreeExpansion = async (clientId, folderPath) => {
    const treeKey = `${clientId}/${folderPath}`;
    
    const newExpanded = new Set(expandedFoldersTree);
    
    if (newExpanded.has(treeKey)) {
      // Collapse
      newExpanded.delete(treeKey);
      setExpandedFoldersTree(newExpanded);
    } else {
      // Expand - first set expanded state, then fetch
      newExpanded.add(treeKey);
      setExpandedFoldersTree(newExpanded);
      
      // Fetch subfolders (will update cache)
      await fetchSubfoldersForTree(clientId, folderPath);
    }
  };

  // Navigate to a folder from tree
  const navigateToFolderFromTree = (clientName, folderPath) => {
    const newPath = [clientName, ...folderPath.split('/').filter(p => p)];
    setSelectedPath(newPath);
    setRecordFiles([]);
    
    const clientId = clientFolders.find(c => c.name === clientName)?.id;
    if (clientId) {
      const subPath = newPath.slice(1).join('/');
      fetchClientFiles(clientId, subPath);
    }
  };

  // Refresh folder tree - clear all caches and let user re-expand
  const handleRefreshTree = async () => {
    setRefreshingTree(true);
    
    try {
      // Simply clear everything - folders will be re-fetched when user expands them
      setFolderTreeCache({});
      setLoadingFolders(new Set());
      setExpandedFoldersTree(new Set());
      
      // Refresh main view if viewing a folder
      if (selectedPath.length > 0) {
        const clientName = selectedPath[0];
        const client = clientFolders.find(c => c.name === clientName);
        if (client) {
          const subPath = selectedPath.slice(1).join('/');
          await fetchClientFiles(client.id, subPath);
        }
      }
    } catch (error) {
      console.error('Error refreshing tree:', error);
    } finally {
      setRefreshingTree(false);
    }
  };


  // Create subfolder in tree
  const handleCreateSubfolder = async () => {
    if (!newSubfolderName.trim()) {
      alert('Please enter a folder name');
      return;
    }
    
    const { clientId, folderPath, clientName } = createSubfolderParent;
    
    try {
      // Construct folder_path - if folderPath exists, append new folder name
      const fullFolderPath = folderPath 
        ? `${folderPath}/${newSubfolderName.trim()}`
        : newSubfolderName.trim();
      
      const url = `${API_BASE_URL}/api/records/clients/${clientId}/create-folder`;
      
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ folder_path: fullFolderPath })
      });

      if (response.ok) {
        // Clear cache for this folder to force refresh
        const cacheKey = `${clientId}/${folderPath}`;
        
        // Clear the cache first
        setFolderTreeCache(prev => {
          const newCache = { ...prev };
          delete newCache[cacheKey];
          return newCache;
        });
        
        // Remove from loading state to ensure fresh fetch
        setLoadingFolders(prev => {
          const newSet = new Set(prev);
          newSet.delete(cacheKey);
          return newSet;
        });
        
        // Make sure the parent folder is expanded in the tree
        const treeKey = folderPath ? `${clientId}/${folderPath}` : `client-${clientId}`;
        setExpandedFoldersTree(prev => {
          const newExpanded = new Set(prev);
          newExpanded.add(treeKey);
          return newExpanded;
        });
        
        // Force refresh by clearing cache and refetching
        // Use a small delay to ensure state updates are processed
        await new Promise(resolve => setTimeout(resolve, 100));
        
        // Refresh the folder tree by fetching subfolders
        await fetchSubfoldersForTree(clientId, folderPath);
        
        // Close modal and reset
        setShowCreateSubfolderModal(false);
        setNewSubfolderName('');
        
        // If currently viewing this folder, refresh main view
        const currentPath = selectedPath.join('/');
        const parentPath = folderPath ? `${clientName}/${folderPath}` : clientName;
        if (currentPath === parentPath || currentPath.startsWith(parentPath + '/')) {
          const subPathForFetch = selectedPath.slice(1).join('/');
          await fetchClientFiles(clientId, subPathForFetch);
        }
      } else {
        const error = await response.json();
        alert(error.detail || 'Failed to create folder');
      }
    } catch (error) {
      console.error('Error creating subfolder:', error);
      alert('Failed to create folder');
    }
  };




  // Move folder within the same client
  const handleMoveFolder = async (folderToMove, destinationKey, clientId, currentPath) => {
    try {
      // Construct source_folder_key: Client Records/{clientId}/{folderPath}/
      const sourceFolderKey = `Client Records/${clientId}/${currentPath}/`;
      
      // destinationKey comes from folder.key which has bucket prefix like: ah/pvah1000/Client Records/...
      // Backend needs it WITHOUT bucket prefix, so strip it if present
      let destinationFolderKey = destinationKey;
      if (destinationFolderKey.includes('Client Records/')) {
        // Extract everything from 'Client Records/' onwards
        destinationFolderKey = destinationFolderKey.substring(destinationFolderKey.indexOf('Client Records/'));
      }
      
      // Make sure it ends with /
      destinationFolderKey = destinationFolderKey.endsWith('/') ? destinationFolderKey : destinationFolderKey + '/';
      
      const response = await fetch(`${API_BASE_URL}/api/records/clients/${clientId}/move-folder`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          source_folder_key: sourceFolderKey,
          destination_folder_key: destinationFolderKey
        })
      });
      
      if (response.ok) {
        // Refresh the current view
        const subPath = selectedPath.slice(1).join('/');
        await fetchClientFiles(clientId, subPath);
        
        // Refresh the entire tree
        await handleRefreshTree();
      } else {
        const error = await response.json();
        alert(error.detail || 'Failed to move folder');
      }
    } catch (error) {
      alert('Failed to move folder');
    }
  };

  // Drag and drop handlers
  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (selectedPath.length > 0) {
      setIsDragging(true);
    }
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    // Only set dragging to false if we're leaving the drop zone completely
    if (e.currentTarget === e.target) {
      setIsDragging(false);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    if (selectedPath.length === 0) return;

    const files = Array.from(e.dataTransfer.files);
    const clientId = clientFolders.find(c => c.name === selectedPath[0])?.id;
    
    if (clientId && files.length > 0) {
      for (const file of files) {
        await uploadClientFile(clientId, file);
      }
    }
  };

  // Add Customer Modal Functions
  const addPet = () => {
    setCustomerFormData({
      ...customerFormData,
      pets: [...customerFormData.pets, { name: '' }]
    });
  };

  const removePet = (index) => {
    const updatedPets = customerFormData.pets.filter((_, i) => i !== index);
    setCustomerFormData({ ...customerFormData, pets: updatedPets });
  };

  const updatePetName = (index, name) => {
    const updatedPets = [...customerFormData.pets];
    updatedPets[index] = { name };
    setCustomerFormData({ ...customerFormData, pets: updatedPets });
  };

  const resetCustomerForm = () => {
    setCustomerFormData({
      first_name: '',
      last_name: '',
      pets: [{ name: '' }],
      phone: '',
      email: '',
      sms_opt_in: true,
      email_subscribed: true
    });
    setShowAddCustomerModal(false);
  };

  const handleAddCustomer = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await fetch(`${API_BASE_URL}/api/customers`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(customerFormData)
      });

      if (response.ok) {
        resetCustomerForm();
        // Refresh client folders to show the new customer
        await fetchClientFolders();
      } else {
        const errorData = await response.json();
        console.error('Failed to add customer:', errorData.detail);
      }
    } catch (error) {
      console.error('Error adding customer:', error);
    } finally {
      setLoading(false);
    }
  };

  // Create folder in client directory
  const handleCreateFolder = async () => {
    if (!newFolderName.trim() || selectedPath.length === 0) return;
    
    setLoading(true);
    try {
      const clientId = clientFolders.find(c => c.name === selectedPath[0])?.id;
      if (!clientId) return;
      
      // Build folder path (client + any subfolders + new folder name)
      const subPath = selectedPath.slice(1).join('/');
      const folderPath = subPath ? `${subPath}/${newFolderName.trim()}` : newFolderName.trim();
      
      const response = await fetch(`${API_BASE_URL}/api/records/clients/${clientId}/create-folder`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ folder_path: folderPath })
      });

      if (response.ok) {
        setNewFolderName('');
        setShowCreateFolderModal(false);
        // Refresh current view
        await fetchClientFiles(clientId, subPath);
      } else {
        console.error('Failed to create folder');
      }
    } catch (error) {
      console.error('Error creating folder:', error);
    } finally {
      setLoading(false);
    }
  };

  // Handle delete confirmation
  const handleDeleteConfirm = async () => {
    if (!itemToDelete) return;
    
    setLoading(true);
    try {
      // Try to get clientId from itemToDelete first (for tree context menu), fallback to selectedPath
      let clientId = itemToDelete.clientId;
      if (!clientId) {
        clientId = clientFolders.find(c => c.name === selectedPath[0])?.id;
      }
      
      if (!clientId) {
        alert('Error: Could not determine client ID');
        return;
      }
      
      if (itemToDelete.type === 'folder') {
        // The folder key from backend includes bucket prefix (ah/pvah1000/)
        // but the delete endpoint expects it WITHOUT the prefix
        let folderKeyToDelete = itemToDelete.key;
        if (folderKeyToDelete.includes('Client Records/')) {
          // Strip bucket prefix if present
          folderKeyToDelete = folderKeyToDelete.substring(folderKeyToDelete.indexOf('Client Records/'));
        }
        
        // Delete folder
        const response = await fetch(`${API_BASE_URL}/api/records/clients/${clientId}/delete-folder`, {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({ folder_key: folderKeyToDelete })
        });
        
        if (response.ok) {
          // Refresh current folder view (not root)
          const subPath = selectedPath.slice(1).join('/');
          await fetchClientFiles(clientId, subPath);
          // Refresh the entire tree
          await handleRefreshTree();
        } else {
          const error = await response.json();
          alert(`Delete failed: ${error.detail || 'Unknown error'}`);
        }
      } else {
        // Delete file
        await deleteClientFile(clientId, itemToDelete.key);
      }
      
      setShowDeleteConfirmModal(false);
      setItemToDelete(null);
    } catch (error) {
      console.error('Error deleting item:', error);
      alert(`Error: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Handle rename folder
  const handleRenameFolder = async () => {
    if (!folderToRename || !renameFolderValue.trim()) return;
    
    setLoading(true);
    try {
      // Try to get clientId from folderToRename first (for tree context menu), fallback to selectedPath
      let clientId = folderToRename.clientId;
      if (!clientId) {
        clientId = clientFolders.find(c => c.name === selectedPath[0])?.id;
      }
      
      if (!clientId) return;
      
      // Strip bucket prefix if present (right panel folders have it, tree folders don't)
      let folderKeyToRename = folderToRename.key;
      if (folderKeyToRename.includes('Client Records/')) {
        folderKeyToRename = folderKeyToRename.substring(folderKeyToRename.indexOf('Client Records/'));
      }
      
      const response = await fetch(`${API_BASE_URL}/api/records/clients/${clientId}/rename-folder`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ 
          old_folder_key: folderKeyToRename,
          new_folder_name: renameFolderValue.trim()
        })
      });
      
      if (response.ok) {
        setShowRenameFolderModal(false);
        setFolderToRename(null);
        setRenameFolderValue('');
        
        // Refresh main view
        const subPath = selectedPath.slice(1).join('/');
        await fetchClientFiles(clientId, subPath);
        
        // Refresh the entire tree
        await handleRefreshTree();
      } else {
        const errorData = await response.json();
        alert(`Rename failed: ${errorData.detail || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error renaming folder:', error);
      alert(`Error: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Handle rename file
  const handleRenameFile = async () => {
    if (!fileToRename || !renameFileValue.trim()) return;
    
    setLoading(true);
    try {
      const clientId = clientFolders.find(c => c.name === selectedPath[0])?.id;
      if (!clientId) return;
      
      // Get the original filename and extract extension
      const originalName = fileToRename.original_name || fileToRename.name;
      const extension = originalName.includes('.') ? originalName.substring(originalName.lastIndexOf('.')) : '';
      
      // Combine new name with original extension
      const newFilename = renameFileValue.trim() + extension;
      
      const response = await fetch(`${API_BASE_URL}/api/records/clients/${clientId}/rename-file`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ 
          file_key: fileToRename.key,
          new_filename: newFilename
        })
      });
      
      if (response.ok) {
        setShowRenameFileModal(false);
        setFileToRename(null);
        setRenameFileValue('');
        await fetchClientFiles(clientId);
      } else {
        console.error('Failed to rename file');
      }
    } catch (error) {
      console.error('Error renaming file:', error);
    } finally {
      setLoading(false);
    }
  };

  // Load available folders for move operation
  const loadAvailableFolders = async (clientId, currentPath = []) => {
    try {
      const subPath = currentPath.join('/');
      const url = subPath 
        ? `${API_BASE_URL}/api/records/clients/${clientId}/files?sub_path=${encodeURIComponent(subPath)}`
        : `${API_BASE_URL}/api/records/clients/${clientId}/files`;
      
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        // Filter only folders
        return (data.folders || []);
      }
      return [];
    } catch (error) {
      console.error('Error loading folders:', error);
      return [];
    }
  };

  // Handle move file
  const handleMoveFile = async (draggedFile, destinationFolderKey) => {
    if (!draggedFile) return;
    
    // Get client ID from the file object (should have been set when opening modal)
    const clientId = draggedFile.clientId || clientFolders.find(c => c.name === selectedPath[0])?.id;
    if (!clientId) {
      showNotification('Client not found. Please try again.', 'error');
      return;
    }
    
    setLoading(true);
    try {
      // Check if it's a folder or file
      const isFolder = draggedFile.type === 'folder';
      const endpoint = isFolder ? 'move-folder' : 'move-file';
      const payload = isFolder ? {
        source_folder_key: draggedFile.key,
        destination_folder_key: destinationFolderKey
      } : {
        file_key: draggedFile.key,
        destination_folder: destinationFolderKey
      };
      
      console.log('Moving:', { isFolder, clientId, endpoint, payload });
      
      const response = await fetch(`${API_BASE_URL}/api/records/clients/${clientId}/${endpoint}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });
      
      if (response.ok) {
        showNotification(`${isFolder ? 'Folder' : 'File'} moved successfully`, 'success');
        setShowMoveFileModal(false);
        setFileToMove(null);
        setMoveDestinationPath([]);
        setMoveDestinationKey('');
        
        // Refresh current folder view
        const subPath = selectedPath.slice(1).join('/');
        await fetchClientFiles(clientId, subPath);
        
        // Refresh the entire tree
        await handleRefreshTree();
      } else {
        const errorData = await response.json();
        console.error(`Failed to move ${isFolder ? 'folder' : 'file'}:`, errorData);
        showNotification(errorData.detail || `Failed to move ${isFolder ? 'folder' : 'file'}. Please try again.`, 'error');
      }
    } catch (error) {
      console.error('Error moving:', error);
      showNotification('An unexpected error occurred. Please try again.', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Fetch recycle bin items
  const fetchRecycleBin = async (clientId) => {
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/records/clients/${clientId}/recycle-bin`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setRecycleBinItems(data.items || []);
      } else {
        console.error('Failed to fetch recycle bin');
        setRecycleBinItems([]);
      }
    } catch (error) {
      console.error('Error fetching recycle bin:', error);
      setRecycleBinItems([]);
    } finally {
      setLoading(false);
    }
  };

  // Browse into a deleted folder in recycle bin
  const browseDeletingFolder = async (clientId, folderName, subPath = '') => {
    setLoading(true);
    try {
      const url = subPath
        ? `${API_BASE_URL}/api/records/clients/${clientId}/recycle-bin/folder/${folderName}/browse?sub_path=${encodeURIComponent(subPath)}`
        : `${API_BASE_URL}/api/records/clients/${clientId}/recycle-bin/folder/${folderName}/browse`;
      
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        // Combine files and folders
        const allItems = [...(data.folders || []), ...(data.files || [])];
        setRecycleBinItems(allItems);
        setRecycleBinBrowseFolder(folderName);
      } else {
        console.error('Failed to browse deleted folder');
        showNotification('Failed to browse folder', 'error');
      }
    } catch (error) {
      console.error('Error browsing deleted folder:', error);
      showNotification('Error browsing folder', 'error');
    } finally {
      setLoading(false);
    }
  };


  // Browse into a deleted folder in global recycle bin
  const browseGlobalDeletedFolder = async (clientId, folderName, subPath = '') => {
    setLoading(true);
    try {
      const subPathParam = subPath ? `?sub_path=${encodeURIComponent(subPath)}` : '';
      const response = await fetch(
        `${API_BASE_URL}/api/records/clients/${clientId}/recycle-bin/folder/${folderName}/browse${subPathParam}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (response.ok) {
        const data = await response.json();
        // Combine files and folders
        const allItems = [...(data.folders || []), ...(data.files || [])];
        setGlobalRecycleBinItems(allItems);
        setGlobalRecycleBinBrowseFolder(folderName);
        setGlobalRecycleBinBrowseClient(clientId);
      } else {
        console.error('Failed to browse deleted folder');
        showNotification('Failed to browse folder', 'error');
      }
    } catch (error) {
      console.error('Error browsing deleted folder:', error);
      showNotification('Error browsing folder', 'error');
    } finally {
      setLoading(false);
    }
  };


  // Fetch global recycle bin (all clients)
  const fetchGlobalRecycleBin = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/records/clients/recycle-bin/all`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setGlobalRecycleBinItems(data.items || []);
      } else {
        console.error('Failed to fetch global recycle bin');
        setGlobalRecycleBinItems([]);
      }
    } catch (error) {
      console.error('Error fetching global recycle bin:', error);
      setGlobalRecycleBinItems([]);
    } finally {
      setLoading(false);
    }
  };

  // Fetch file versions
  const fetchFileVersions = async (clientId, fileKey, fileName) => {
    setLoading(true);
    try {
      const encodedKey = encodeURIComponent(fileKey);
      const response = await fetch(`${API_BASE_URL}/api/records/clients/${clientId}/files/${encodedKey}/versions`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setSelectedFileVersions(data.versions || []);
        setSelectedFileName(data.original_filename || fileName);
        setShowVersionsModal(true);
      } else {
        console.error('Failed to fetch file versions');
      }
    } catch (error) {
      console.error('Error fetching file versions:', error);
    } finally {
      setLoading(false);
    }
  };

  // Restore from recycle bin - show confirmation first
  const handleRestoreItem = async (item) => {
    setItemToRestore(item);
    setIsGlobalRestore(false);
    setShowRestoreConfirmModal(true);
  };

  // Restore from global recycle bin - show confirmation first
  const handleRestoreGlobalItem = async (item) => {
    setItemToRestore(item);
    setIsGlobalRestore(true);
    setShowRestoreConfirmModal(true);
  };

  // Confirm and execute restore
  const confirmRestore = async () => {
    const item = itemToRestore;
    if (!item) return;

    setLoading(true);
    setShowRestoreConfirmModal(false);

    try {
      let clientId;
      
      if (isGlobalRestore) {
        // Global restore
        if (globalRecycleBinBrowseClient) {
          // When browsing, use the stored client ID
          clientId = globalRecycleBinBrowseClient;
        } else if (item.client_id) {
          // When not browsing, use item's client ID
          clientId = item.client_id;
        } else {
          showNotification('Cannot restore: Client information missing', 'error');
          setLoading(false);
          setItemToRestore(null);
          return;
        }
      } else {
        // Client-specific restore
        if (!selectedPath[0]) return;
        clientId = clientFolders.find(c => c.name === selectedPath[0])?.id;
        if (!clientId) {
          showNotification('Client not found', 'error');
          return;
        }
      }
      
      if (!item.key) {
        showNotification('Cannot restore item: Invalid item key', 'error');
        return;
      }
      
      const response = await fetch(`${API_BASE_URL}/api/records/clients/${clientId}/recycle-bin/restore`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ item_key: item.key })
      });

      if (response.ok) {
        // Successfully restored - no toast notification, just refresh
        
        if (isGlobalRestore) {
          // Browsing global recycle bin - clear state and go back
          if (globalRecycleBinBrowseFolder) {
            setGlobalRecycleBinBrowseFolder(null);
            setGlobalRecycleBinBrowseClient(null);
            setGlobalRecycleBinBrowsePath([]);
          }
          
          // Refresh global recycle bin
          await fetchGlobalRecycleBin();
          
          // If currently viewing the client where file was restored, refresh it
          const currentClientId = clientFolders.find(c => c.name === selectedPath[0])?.id;
          if (currentClientId === clientId) {
            const subPath = selectedPath.slice(1).join('/');
            await fetchClientFiles(currentClientId, subPath);
          }
        } else {
          // Refresh client-specific recycle bin
          if (recycleBinBrowseFolder) {
            setRecycleBinBrowseFolder(null);
            setRecycleBinBrowsePath([]);
          }
          await fetchRecycleBin(clientId);
          
          // Auto-refresh current folder view
          const subPath = selectedPath.slice(1).join('/');
          await fetchClientFiles(clientId, subPath);
        }
      } else {
        const errorData = await response.json();
        showNotification(`Failed to restore: ${errorData.detail || 'Unknown error'}`, 'error');
      }
    } catch (error) {
      console.error('Error restoring item:', error);
      showNotification('An error occurred while restoring. Please try again.', 'error');
    } finally {
      setLoading(false);
      setItemToRestore(null);
    }
  };

  // Permanently delete from recycle bin
  const handlePermanentDelete = async (item) => {
    if (!selectedPath[0]) return;
    
    if (!window.confirm(`Are you sure you want to PERMANENTLY delete "${item.name}"? This action cannot be undone.`)) {
      return;
    }
    
    setLoading(true);
    try {
      const clientId = clientFolders.find(c => c.name === selectedPath[0])?.id;
      if (!clientId) return;
      
      const response = await fetch(`${API_BASE_URL}/api/records/clients/${clientId}/recycle-bin/permanent-delete`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ item_key: item.key })
      });

      if (response.ok) {
        await fetchRecycleBin(clientId);
      } else {
        console.error('Failed to permanently delete item');
      }
    } catch (error) {
      console.error('Error permanently deleting item:', error);
    } finally {
      setLoading(false);
    }
  };

  // Permanently delete from global recycle bin
  const handlePermanentDeleteGlobal = async (item) => {
    if (!window.confirm(`Are you sure you want to PERMANENTLY delete "${item.name}" from ${item.client_name}? This action cannot be undone.`)) {
      return;
    }
    
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/records/clients/${item.client_id}/recycle-bin/permanent-delete`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ item_key: item.key })
      });

      if (response.ok) {
        await fetchGlobalRecycleBin();
      } else {
        console.error('Failed to permanently delete item');
      }
    } catch (error) {
      console.error('Error permanently deleting item:', error);
    } finally {
      setLoading(false);
    }
  };

  // Helper functions
  const isPreviewable = (fileName) => {
    const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.bmp', '.webp', '.svg'];
    const pdfExtensions = ['.pdf'];
    const officeExtensions = ['.doc', '.docx', '.xls', '.xlsx', '.ppt', '.pptx'];
    const extension = fileName.toLowerCase().substring(fileName.lastIndexOf('.'));
    return imageExtensions.includes(extension) || pdfExtensions.includes(extension) || officeExtensions.includes(extension);
  };

  const isImageFile = (fileName) => {
    const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.bmp', '.webp', '.svg'];
    const extension = fileName.toLowerCase().substring(fileName.lastIndexOf('.'));
    return imageExtensions.includes(extension);
  };

  const getFileType = (fileName) => {
    const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.bmp', '.webp', '.svg'];
    const officeExtensions = ['.doc', '.docx', '.xls', '.xlsx', '.ppt', '.pptx'];
    const extension = fileName.toLowerCase().substring(fileName.lastIndexOf('.'));
    if (imageExtensions.includes(extension)) return 'image';
    if (['.pdf'].includes(extension)) return 'pdf';
    if (officeExtensions.includes(extension)) return 'office';
    return 'unknown';
  };

  // Get files grouped by original filename for version count
  const getFilesWithVersionCount = () => {
    const files = filteredRecords.filter(r => r.type === 'file');
    const folders = filteredRecords.filter(r => r.type === 'folder');
    
    // Group files by original_name to count versions
    const fileVersionCounts = {};
    files.forEach(file => {
      const originalName = file.original_name || file.name;
      if (!fileVersionCounts[originalName]) {
        fileVersionCounts[originalName] = [];
      }
      fileVersionCounts[originalName].push(file);
    });
    
    // Get unique files (latest version of each)
    const uniqueFiles = [];
    const processedNames = new Set();
    
    files.forEach(file => {
      const originalName = file.original_name || file.name;
      if (!processedNames.has(originalName)) {
        const versions = fileVersionCounts[originalName];
        // Sort by version (descending) to get latest
        versions.sort((a, b) => (b.version || 0) - (a.version || 0));
        const latestVersion = versions[0];
        
        uniqueFiles.push({
          ...latestVersion,
          versionCount: versions.length,
          hasMultipleVersions: versions.length > 1
        });
        processedNames.add(originalName);
      }
    });
    
    return [...folders, ...uniqueFiles];
  };

  const getFileIcon = (filename, size = 'default') => {
    const ext = filename.split('.').pop().toLowerCase();
    const sizeClass = size === 'large' ? 'h-12 w-12' : 'h-8 w-8';
    
    // Return icon with extension badge
    const renderIconWithExtension = (Icon, color) => {
      if (size === 'large') {
        return (
          <div className="relative">
            <Icon className={`${sizeClass} ${color}`} />
            <div className={`absolute -bottom-1 -right-1 bg-white border border-gray-300 rounded px-1 text-[10px] font-bold ${color.replace('text-', 'text-')}`}>
              .{ext}
            </div>
          </div>
        );
      } else {
        return (
          <div className="flex flex-col items-center gap-0.5">
            <Icon className={`${sizeClass} ${color}`} />
            <span className={`text-[9px] font-semibold ${color}`}>.{ext}</span>
          </div>
        );
      }
    };
    
    // Image files
    if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'bmp', 'ico'].includes(ext)) {
      return renderIconWithExtension(FileImage, 'text-green-500');
    } 
    // PDF files
    else if (ext === 'pdf') {
      return renderIconWithExtension(FileText, 'text-red-500');
    } 
    // Word documents
    else if (['doc', 'docx'].includes(ext)) {
      return renderIconWithExtension(FileText, 'text-theme-blue');
    } 
    // Excel spreadsheets
    else if (['xls', 'xlsx', 'csv'].includes(ext)) {
      return renderIconWithExtension(FileSpreadsheet, 'text-green-600');
    } 
    // PowerPoint presentations
    else if (['ppt', 'pptx'].includes(ext)) {
      return renderIconWithExtension(FileText, 'text-orange-600');
    } 
    // Video files
    else if (['mp4', 'avi', 'mov', 'wmv', 'flv', 'mkv', 'webm'].includes(ext)) {
      return renderIconWithExtension(FileVideo, 'text-purple-500');
    } 
    // Audio files
    else if (['mp3', 'wav', 'aac', 'flac', 'ogg', 'm4a'].includes(ext)) {
      return renderIconWithExtension(FileAudio, 'text-pink-500');
    } 
    // Archive files
    else if (['zip', 'rar', '7z', 'tar', 'gz'].includes(ext)) {
      return renderIconWithExtension(FileArchive, 'text-yellow-600');
    } 
    // Code files
    else if (['js', 'jsx', 'ts', 'tsx', 'py', 'java', 'cpp', 'c', 'html', 'css', 'json', 'xml'].includes(ext)) {
      return renderIconWithExtension(FileCode, 'text-gray-600');
    } 
    // Text files
    else if (['txt', 'md', 'log'].includes(ext)) {
      return renderIconWithExtension(FileText, 'text-gray-500');
    } 
    // Default file icon
    else {
      return renderIconWithExtension(File, 'text-gray-400');
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const copyUrlToClipboard = (url) => {
    navigator.clipboard.writeText(url).then(() => {
      setCopiedUrl(true);
      setTimeout(() => setCopiedUrl(false), 2000);
    }).catch(err => {
      console.error('Failed to copy URL:', err);
    });
  };

  const openPreview = (file) => {
    const fileType = getFileType(file.name);
    let fileUrl = file.url;
    
    // For PDF and Office files, use our backend proxy to avoid CORS issues
    if ((fileType === 'pdf' || fileType === 'office') && file.key) {
      fileUrl = `${API_BASE_URL}/api/records/proxy-pdf?s3_key=${encodeURIComponent(file.key)}`;
    }
    
    setPreviewData({
      url: fileUrl,
      name: file.name,
      type: fileType,
      s3Key: file.key // Store S3 key for potential use
    });
    setShowPreviewModal(true);
  };

  const organizedClients = organizeClientsByLetter();

  // Auto-expand folders containing search results
  useEffect(() => {
    if (searchTerm.trim()) {
      const newExpanded = new Set(expandedFolders);
      
      // Find all letters that might have matching clients
      Object.keys(letterCounts).forEach(async (letter) => {
        // Load this letter if not already loaded
        if (!loadedLetters[letter]) {
          await loadClientsForLetter(letter);
        }
        
        // Check if any clients in this letter match the search
        const clients = organizedClients[letter] || [];
        const hasMatch = clients.some(client =>
          client.name.toLowerCase().includes(searchTerm.toLowerCase())
        );
        
        if (hasMatch) {
          newExpanded.add(`letter-${letter}`);
        }
      });
      
      setExpandedFolders(newExpanded);
    }
  }, [searchTerm]);

  // Filter files/folders based on selected filter mode
  const getFilteredRecords = () => {
    if (filterMode === 'files') {
      return recordFiles.filter(item => item.type === 'file');
    } else if (filterMode === 'folders') {
      return recordFiles.filter(item => item.type === 'folder');
    }
    return recordFiles; // 'both' - show all
  };

  const filteredRecords = getFilteredRecords();


  // Recursive Folder Tree Item Component
  const FolderTreeItem = ({ clientId, clientName, folderName, folderPath, level = 0 }) => {
    const treeKey = `${clientId}/${folderPath}`;
    const isExpanded = expandedFoldersTree.has(treeKey);
    const isLoading = loadingFolders.has(`${clientId}/${folderPath}`);
    const subfolders = folderTreeCache[`${clientId}/${folderPath}`] || [];
    
    // Check if this folder is the exact selected folder (not parent)
    const currentSubPath = selectedPath.slice(1).join('/');
    const isSelected = currentSubPath === folderPath;
    
    const hasSubfolders = subfolders.length > 0 || !folderTreeCache[`${clientId}/${folderPath}`];
    
    return (
      <div className="select-none">
        <div
          className={`flex items-center px-2 py-1.5 rounded cursor-pointer group ${
            isSelected 
              ? 'bg-blue-100 text-blue-700' 
              : 'hover:bg-gray-100 text-gray-700'
          }`}
          style={{ paddingLeft: `${level * 16 + 8}px` }}
          onContextMenu={(e) => {
            e.preventDefault();
            e.stopPropagation();
            
            // Construct the full S3 key for this folder
            const folderKey = `Client Records/${clientId}/${folderPath}/`;
            
            setTreeContextMenu({
              show: true,
              x: e.clientX,
              y: e.clientY,
              folder: { 
                name: folderName,
                key: folderKey,
                type: 'folder'
              },
              clientId: clientId,
              folderPath: folderPath,
              clientName: clientName
            });
          }}
        >
          {/* Chevron icon for expand/collapse */}
          {hasSubfolders && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                toggleFolderTreeExpansion(clientId, folderPath);
              }}
              className="mr-1 p-0.5 hover:bg-gray-200 rounded"
            >
              {isLoading ? (
                <RefreshCw className="h-3.5 w-3.5 text-gray-400 animate-spin" />
              ) : isExpanded ? (
                <ChevronDown className="h-3.5 w-3.5 text-gray-500" />
              ) : (
                <ChevronRight className="h-3.5 w-3.5 text-gray-500" />
              )}
            </button>
          )}
          
          {/* Folder icon and name */}
          <div
            onClick={() => navigateToFolderFromTree(clientName, folderPath)}
            className="flex items-center flex-1 min-w-0"
          >
            <Folder className={`h-3.5 w-3.5 mr-1.5 flex-shrink-0 ${
              isSelected ? 'text-theme-blue' : 'text-gray-500'
            }`} />
            <span className="text-sm truncate">{folderName}</span>
          </div>
        </div>
        
        {/* Render subfolders if expanded */}
        {isExpanded && subfolders.length > 0 && (
          <div>
            {subfolders.map((subfolder) => {
              const subfolderPath = folderPath ? `${folderPath}/${subfolder.name}` : subfolder.name;
              return (
                <FolderTreeItem
                  key={subfolder.name}
                  clientId={clientId}
                  clientName={clientName}
                  folderName={subfolder.name}
                  folderPath={subfolderPath}
                  level={level + 1}
                />
              );
            })}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Success Toast */}
      {copiedUrl && (
        <div className="fixed top-4 right-4 z-50 bg-green-500 text-white px-6 py-3 rounded-lg shadow-lg flex items-center space-x-2">
          <CheckCircle className="h-5 w-5" />
          <span className="font-medium">URL copied to clipboard!</span>
        </div>
      )}

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
        <div className="bg-white rounded-xl shadow-lg overflow-visible flex flex-col" style={{ height: 'calc(100vh - 120px)' }}>
          {/* Header - Inside the main card, matching Business Info style */}
          <div className="px-6 py-4 bg-gray-50 border-b border-gray-200 relative z-10 flex-shrink-0">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900">
                Client Records
              </h2>
              <button
                onClick={() => {
                  setShowGlobalRecycleBin(true);
                  fetchGlobalRecycleBin();
                }}
                className="px-2.5 py-1.5 text-xs text-gray-600 border border-gray-300 rounded hover:bg-gray-50 transition-colors flex items-center space-x-1.5"
                title="View All Deleted Items"
              >
                <Trash2 className="h-3.5 w-3.5" />
                <span>Recycle Bin</span>
              </button>
            </div>
          </div>

          {/* Breadcrumb Path - Sticky below top nav */}
          <div className="sticky z-40 bg-gray-100 border-b border-gray-300 px-6 py-3 shadow-sm flex-shrink-0 flex items-center justify-between" style={{ top: '103px' }}>
            <div className="flex items-center text-sm text-gray-700 font-medium">
              <Folder className="h-4 w-4 mr-2 text-gray-500" />
              {selectedPath.length === 0 ? (
                'Select a client'
              ) : (
                <div className="flex items-center">
                  {selectedPath.map((segment, index) => (
                    <div key={index} className="flex items-center">
                      {index > 0 && <span className="mx-2 text-gray-400">/</span>}
                      <button
                        onClick={() => navigateToBreadcrumb(index)}
                        className={`hover:text-theme-blue hover:underline ${
                          index === selectedPath.length - 1 ? 'text-gray-900' : 'text-theme-blue'
                        }`}
                      >
                        {segment}
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <button
              onClick={() => setShowAddCustomerModal(true)}
              className="px-3 py-1.5 text-xs bg-theme-blue text-white rounded hover:bg-blue-700 transition-colors flex items-center space-x-1.5"
            >
              <Plus className="h-3.5 w-3.5" />
              <span>Add Customer</span>
            </button>
          </div>

          {/* Search and Toolbar Row - Sticky below breadcrumb */}
          <div className="sticky z-40 flex bg-white border-b border-gray-200 flex-shrink-0" style={{ top: '151px' }}>
            {/* Search - Left side */}
            <div className="relative" style={{ width: `${leftPanelWidth}px` }}>
              <Search className="absolute left-6 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 z-10" />
              <input
                type="text"
                placeholder="Search clients..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-14 pr-6 py-3.5 text-sm bg-white border-0 border-r border-gray-200 focus:outline-none focus:ring-0"
              />
            </div>

            {/* Resizer Handle - Visual separator */}
            <div className="w-1 bg-gray-300" />

            {/* Toolbar - Right side */}
            <div className="flex-1 px-6 flex items-center justify-between" style={{ paddingTop: '0.625rem', paddingBottom: '0.48rem' }}>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => {
                    if (selectedPath.length > 0) {
                      const clientId = clientFolders.find(c => c.name === selectedPath[0])?.id;
                      if (clientId) fetchClientFiles(clientId);
                    }
                  }}
                  disabled={loading || selectedPath.length === 0}
                  className="px-2.5 py-1.5 text-xs text-gray-600 border border-gray-300 rounded hover:bg-gray-50 transition-colors flex items-center space-x-1.5 disabled:opacity-50"
                >
                  <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
                  <span>Refresh</span>
                </button>
                
                {selectedPath.length > 0 && (
                  <label className="px-2.5 py-1.5 text-xs bg-theme-blue text-white rounded hover:bg-blue-700 transition-colors cursor-pointer flex items-center space-x-1.5">
                    <Upload className="h-3.5 w-3.5" />
                    <span>Upload Files</span>
                    <input
                      type="file"
                      multiple
                      onChange={async (e) => {
                        const files = Array.from(e.target.files);
                        const clientId = clientFolders.find(c => c.name === selectedPath[0])?.id;
                        if (clientId) {
                          for (const file of files) {
                            await uploadClientFile(clientId, file);
                          }
                        }
                        e.target.value = '';
                      }}
                      className="hidden"
                    />
                  </label>
                )}

                {selectedPath.length > 0 && (
                  <button
                    onClick={() => setShowCreateFolderModal(true)}
                    className="px-2.5 py-1.5 text-xs text-green-600 border border-green-300 rounded hover:bg-green-50 transition-colors flex items-center space-x-1.5"
                  >
                    <FolderPlus className="h-3.5 w-3.5" />
                    <span>New Folder</span>
                  </button>
                )}

                {selectedPath.length > 0 && (
                  <button
                    onClick={() => {
                      const clientId = clientFolders.find(c => c.name === selectedPath[0])?.id;
                      if (clientId) {
                        setShowRecycleBin(true);
                        fetchRecycleBin(clientId);
                      }
                    }}
                    className="px-2.5 py-1.5 text-xs text-gray-600 border border-gray-300 rounded hover:bg-gray-50 transition-colors flex items-center space-x-1.5"
                    title="Recycle Bin"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                    <span>Recycle Bin</span>
                  </button>
                )}
              </div>
              
              {/* View Mode Toggle */}
              <div className="flex items-center space-x-2">
                {/* Filter Dropdown */}
                <div className="relative">
                  <button
                    onClick={() => setShowFilterDropdown(!showFilterDropdown)}
                    className="p-1.5 border border-gray-300 rounded text-gray-600 hover:bg-gray-100 transition-colors"
                    title="Filter"
                  >
                    <Filter className="h-3.5 w-3.5" />
                  </button>
                  
                  {showFilterDropdown && (
                    <div className="absolute right-0 mt-2 w-48 bg-white border border-gray-200 rounded-lg shadow-lg z-[70]">
                      <div className="py-1">
                        <button
                          onClick={() => {
                            setFilterMode('both');
                            setShowFilterDropdown(false);
                          }}
                          className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-100 flex items-center justify-between ${
                            filterMode === 'both' ? 'bg-blue-50 text-theme-blue' : 'text-gray-700'
                          }`}
                        >
                          <span>Show Both</span>
                          {filterMode === 'both' && <CheckCircle className="h-4 w-4" />}
                        </button>
                        <button
                          onClick={() => {
                            setFilterMode('files');
                            setShowFilterDropdown(false);
                          }}
                          className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-100 flex items-center justify-between ${
                            filterMode === 'files' ? 'bg-blue-50 text-theme-blue' : 'text-gray-700'
                          }`}
                        >
                          <span>Show Files Only</span>
                          {filterMode === 'files' && <CheckCircle className="h-4 w-4" />}
                        </button>
                        <button
                          onClick={() => {
                            setFilterMode('folders');
                            setShowFilterDropdown(false);
                          }}
                          className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-100 flex items-center justify-between ${
                            filterMode === 'folders' ? 'bg-blue-50 text-theme-blue' : 'text-gray-700'
                          }`}
                        >
                          <span>Show Folders Only</span>
                          {filterMode === 'folders' && <CheckCircle className="h-4 w-4" />}
                        </button>
                      </div>
                    </div>
                  )}
                </div>

                {/* View Mode Toggle */}
                <div className="flex border border-gray-300 rounded">
                  <button
                    onClick={() => setViewMode('grid')}
                    className={`p-1.5 ${viewMode === 'grid' ? 'bg-blue-100 text-theme-blue' : 'text-gray-600 hover:bg-gray-100'}`}
                  >
                    <Grid className="h-3.5 w-3.5" />
                  </button>
                  <button
                    onClick={() => setViewMode('list')}
                    className={`p-1.5 ${viewMode === 'list' ? 'bg-blue-100 text-theme-blue' : 'text-gray-600 hover:bg-gray-100'}`}
                  >
                    <List className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Main Content - Two Panel Layout with independent scrolling */}
          <div className="flex flex-1 overflow-hidden">
            {/* Left Navigation Panel - Independently Scrollable */}
            <div 
              className="bg-white border-r border-gray-300 overflow-y-auto flex-shrink-0"
              style={{ width: `${leftPanelWidth}px` }}
            >
            {/* A-Z Folder Tree */}
            <div className="p-2">
              {loading ? (
                <div className="flex items-center justify-center py-8">
                  <RefreshCw className="h-6 w-6 animate-spin text-gray-400" />
                </div>
              ) : (
                <>
                  {Object.keys(organizedClients).sort().map((letter) => {
                    const clients = organizedClients[letter].filter(client =>
                      client.name.toLowerCase().includes(searchTerm.toLowerCase())
                    );
                    
                    // Get the total count from letterCounts (not filtered count)
                    const totalCount = letterCounts[letter] || 0;
                    
                    if (clients.length === 0 && searchTerm) return null;
                    
                    const letterFolderId = `letter-${letter}`;
                    const isExpanded = expandedFolders.has(letterFolderId);
                    
                    return (
                      <div key={letter} className="mb-1">
                        {/* Letter Folder */}
                        <div
                          onClick={() => toggleFolder(letterFolderId)}
                          className="flex items-center px-2 py-2 rounded hover:bg-blue-50 cursor-pointer group"
                        >
                          {isExpanded ? (
                            <ChevronDown className="h-4 w-4 text-gray-500 mr-1" />
                          ) : (
                            <ChevronRight className="h-4 w-4 text-gray-500 mr-1" />
                          )}
                          <Folder className="h-4 w-4 text-theme-blue mr-2" />
                          <span className="text-sm font-semibold text-gray-900">{letter}</span>
                          <span className="ml-auto text-xs text-gray-500 bg-gray-200 px-2 py-0.5 rounded-full">
                            {totalCount}
                          </span>
                        </div>
                        
                        {/* Client Folders under this letter */}
                        {isExpanded && clients.length > 0 && (
                          <div className="ml-5 border-l border-gray-200 pl-2">
                            {clients.map((client) => {
                              const isClientSelected = selectedPath[0] === client.name && selectedPath.length === 1;
                              const clientTreeKey = `client-${client.id}`;
                              const isClientExpanded = expandedFoldersTree.has(clientTreeKey);
                              const rootFolders = folderTreeCache[`${client.id}/`] || [];
                              const isLoadingClientFolders = loadingFolders.has(`${client.id}/`);
                              
                              return (
                                <div key={client.id} className="mb-0.5">
                                  {/* Client row with expand/collapse */}
                                  <div 
                                    className={`flex items-center px-2 py-1.5 rounded cursor-pointer ${
                                      isClientSelected 
                                        ? 'bg-blue-100 text-blue-700' 
                                        : 'hover:bg-gray-100 text-gray-700'
                                    }`}
                                  >
                                    {/* Expand/Collapse chevron for client folders */}
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        const newExpanded = new Set(expandedFoldersTree);
                                        if (newExpanded.has(clientTreeKey)) {
                                          newExpanded.delete(clientTreeKey);
                                        } else {
                                          newExpanded.add(clientTreeKey);
                                          // Fetch root folders if not loaded yet
                                          if (!folderTreeCache[`${client.id}/`] && !loadingFolders.has(`${client.id}/`)) {
                                            fetchSubfoldersForTree(client.id, '');
                                          }
                                        }
                                        setExpandedFoldersTree(newExpanded);
                                      }}
                                      className="mr-1 p-0.5 hover:bg-gray-200 rounded flex-shrink-0"
                                    >
                                      {isLoadingClientFolders ? (
                                        <RefreshCw className="h-3.5 w-3.5 text-gray-400 animate-spin" />
                                      ) : isClientExpanded ? (
                                        <ChevronDown className="h-3.5 w-3.5 text-gray-500" />
                                      ) : (
                                        <ChevronRight className="h-3.5 w-3.5 text-gray-500" />
                                      )}
                                    </button>
                                    
                                    {/* Client name and icon */}
                                    <div
                                      onClick={() => selectClient(client)}
                                      onContextMenu={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        setClientContextMenu({
                                          show: true,
                                          x: e.clientX,
                                          y: e.clientY,
                                          client: client
                                        });
                                      }}
                                      className="flex items-center flex-1 min-w-0"
                                    >
                                      <FolderOpen className={`h-4 w-4 mr-2 flex-shrink-0 ${isClientSelected ? 'text-theme-blue' : 'text-gray-500'}`} />
                                      <span className="text-sm truncate">{client.name}</span>
                                    </div>
                                  </div>
                                  
                                  {/* Nested folder tree under client */}
                                  {isClientExpanded && rootFolders.length > 0 && (
                                    <div className="ml-4 border-l border-gray-200 pl-1">
                                      {rootFolders.map((folder) => (
                                        <FolderTreeItem
                                          key={folder.name}
                                          clientId={client.id}
                                          clientName={client.name}
                                          folderName={folder.name}
                                          folderPath={folder.name}
                                          level={0}
                                        />
                                      ))}
                                    </div>
                                  )}
                                  
                                  {/* Empty state when client expanded but no folders */}
                                  {isClientExpanded && !isLoadingClientFolders && rootFolders.length === 0 && (
                                    <div className="ml-8 text-xs text-gray-400 px-2 py-1">
                                      No folders
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        )}
                        
                        {/* Empty state */}
                        {isExpanded && clients.length === 0 && (
                          <div className="ml-5 text-xs text-gray-400 px-2 py-1">
                            No clients
                          </div>
                        )}
                      </div>
                    );
                  })}
                </>
              )}
            </div>
          </div>

          {/* Resizer Handle */}
          <div
            ref={resizeRef}
            onMouseDown={() => setIsResizing(true)}
            className={`w-1 bg-gray-300 hover:bg-theme-blue cursor-col-resize transition-colors ${
              isResizing ? 'bg-theme-blue' : ''
            }`}
          />

          {/* Right Content Panel - Independently Scrollable */}
          <div className="flex-1 flex flex-col overflow-hidden">
            {/* Files Display Area - Scrollable */}
            <div 
              className="flex-1 overflow-y-auto bg-gray-50 p-6 relative"
              onDragEnter={handleDragEnter}
              onDragLeave={handleDragLeave}
              onDragOver={handleDragOver}
              onDrop={handleDrop}
            >
              {/* Drag and Drop Overlay */}
              {isDragging && selectedPath.length > 0 && (
                <div className="absolute inset-0 bg-theme-blue bg-opacity-20 border-4 border-dashed border-theme-blue flex items-center justify-center z-50">
                  <div className="bg-white rounded-lg p-6 shadow-xl">
                    <Upload className="h-12 w-12 text-theme-blue mx-auto mb-3" />
                    <p className="text-lg font-semibold text-gray-900">Drop files here</p>
                    <p className="text-sm text-gray-600 mt-1">to upload to {selectedPath[0]}</p>
                  </div>
                </div>
              )}

              {selectedPath.length === 0 ? (
                <div className="text-center py-12">
                  <FolderOpen className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600 text-lg">Select a client folder</p>
                  <p className="text-gray-500 text-sm mt-2">Choose a client from the left panel to view their files</p>
                </div>
              ) : loading ? (
                <div className="flex items-center justify-center h-64">
                  <RefreshCw className="h-8 w-8 animate-spin text-gray-400" />
                  <span className="ml-2 text-gray-600">Loading files...</span>
                </div>
              ) : filteredRecords.length === 0 ? (
                <div className="text-center py-12 bg-white rounded-lg border-2 border-dashed border-gray-300">
                  <Upload className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600 text-lg">
                    {filterMode === 'files' ? 'No files found' : filterMode === 'folders' ? 'No folders found' : 'No files or folders found'}
                  </p>
                  <p className="text-gray-500 text-sm mt-2">
                    {filterMode === 'files' ? 'Upload some files to get started' : filterMode === 'folders' ? 'Create a folder to get started' : 'Upload files or create folders to get started'}
                  </p>
                </div>
              ) : (
                <>
                  {viewMode === 'grid' ? (
                    // Grid View
                    <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
                      {getFilesWithVersionCount().map((item) => {
                        const isFolder = item.isFolder || item.type === 'folder';
                        const filename = item.original_name || item.name;
                        const displayName = item.original_name || item.name;
                        const isImage = !isFolder && isImageFile(filename);
                        
                        return (
                          <div
                            key={item.key}
                            className="relative group border border-gray-200 bg-white rounded-lg p-3 hover:border-blue-300 hover:shadow-md transition-all cursor-pointer"
                            title={displayName}
                            onClick={() => {
                              if (isFolder) {
                                navigateIntoFolder(filename);
                              }
                            }}
                          >
                            {/* 3-dot menu button - for both files and folders */}
                            <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity z-10">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setContextMenu({
                                    show: true,
                                    x: e.clientX,
                                    y: e.clientY,
                                    file: item
                                  });
                                }}
                                className="bg-white shadow-md rounded-full p-1 hover:bg-gray-100 transition-colors"
                              >
                                <MoreVertical className="h-4 w-4 text-gray-600" />
                              </button>
                            </div>
                            
                            {/* File/Folder preview */}
                            <div className="aspect-square bg-gray-100 rounded-lg mb-2 overflow-hidden flex items-center justify-center">
                              {isFolder ? (
                                <Folder className="h-12 w-12 text-theme-blue" />
                              ) : (
                                <div>
                                  {getFileIcon(filename, 'large')}
                                </div>
                              )}
                            </div>
                            
                            {/* File/Folder info */}
                            <div className="text-xs space-y-1">
                              <p className="font-medium text-gray-800 truncate text-center">
                                {isFolder ? displayName : (displayName.substring(0, displayName.lastIndexOf('.')) || displayName)}
                              </p>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    // List View
                    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Name
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Type
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Modified
                            </th>
                            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Actions
                            </th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {getFilesWithVersionCount().map((item) => {
                            const isFolder = item.isFolder || item.type === 'folder';
                            const filename = item.original_name || item.name;
                            const displayName = item.original_name || item.name;
                            const isImage = !isFolder && isImageFile(filename);
                            const fileExtension = isFolder ? 'Folder' : filename.split('.').pop().toUpperCase();
                            
                            return (
                              <tr 
                                key={item.key} 
                                className="hover:bg-gray-50 cursor-pointer"
                                onClick={() => {
                                  if (isFolder) {
                                    navigateIntoFolder(filename);
                                  }
                                }}
                              >
                                <td className="px-6 py-4 whitespace-nowrap">
                                  <div className="flex items-center">
                                    {/* Removed older version indicator */}
                                    <div className="text-2xl mr-3">
                                      {isFolder ? <Folder className="h-6 w-6 text-theme-blue" /> : getFileIcon(filename)}
                                    </div>
                                    <div className="flex items-center space-x-2">
                                      <div className="text-sm font-medium text-gray-900 truncate max-w-md" title={displayName}>
                                        {isFolder ? displayName : (displayName.substring(0, displayName.lastIndexOf('.')) || displayName)}
                                      </div>
                                    </div>
                                  </div>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                  {fileExtension}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                  {item.last_modified ? new Date(item.last_modified).toLocaleDateString() : '-'}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setContextMenu({
                                        show: true,
                                        x: e.clientX,
                                        y: e.clientY,
                                        file: item
                                      });
                                    }}
                                    className="text-gray-600 hover:text-gray-900"
                                  >
                                    <MoreVertical className="h-4 w-4" />
                                  </button>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>

      {/* Context Menu */}
      {contextMenu.show && contextMenu.file && (
        <div
          className="fixed bg-white border border-gray-200 rounded-lg shadow-lg py-2 z-[70]"
          style={{ 
            left: `${contextMenu.x}px`, 
            top: `${contextMenu.y}px`,
            transform: 'translate(-50%, 0)'
          }}
        >
          {contextMenu.file.type === 'folder' ? (
            // Folder actions
            <>
              <button
                onClick={() => {
                  // Set up to create subfolder inside this folder
                  const clientId = clientFolders.find(c => c.name === selectedPath[0])?.id;
                  const clientName = selectedPath[0];
                  const currentPath = selectedPath.slice(1).concat(contextMenu.file.name).join('/');
                  setCreateSubfolderParent({
                    clientId: clientId,
                    folderPath: currentPath,
                    clientName: clientName
                  });
                  setShowCreateSubfolderModal(true);
                  setContextMenu({ show: false, x: 0, y: 0, file: null });
                }}
                className="w-full px-4 py-2 text-left text-sm hover:bg-gray-100 flex items-center"
              >
                <FolderPlus className="h-4 w-4 mr-2 text-theme-blue" />
                New Folder
              </button>
              <button
                onClick={() => {
                  setFolderToRename(contextMenu.file);
                  setRenameFolderValue(contextMenu.file.name);
                  setShowRenameFolderModal(true);
                  setContextMenu({ show: false, x: 0, y: 0, file: null });
                }}
                className="w-full px-4 py-2 text-left text-sm hover:bg-gray-100 flex items-center"
              >
                <Edit2 className="h-4 w-4 mr-2" />
                Rename
              </button>
              <button
                onClick={async () => {
                  // Use the same move functionality as Hospital Records (working approach)
                  // Get client ID
                  const clientId = contextMenu.file.clientId || clientFolders.find(c => c.name === selectedPath[0])?.id;
                  
                  if (!clientId) {
                    showNotification('Client not found. Please try again.', 'error');
                    return;
                  }
                  
                  // Add clientId to the file object for handleMoveFile to use
                  const folderWithClientId = {
                    ...contextMenu.file,
                    clientId,
                    type: 'folder'  // Ensure type is set
                  };
                  
                  setFileToMove(folderWithClientId);
                  setMoveDestinationPath([]);
                  setMoveDestinationKey('');
                  
                  // Load current client's folders
                  const folders = await loadAvailableFolders(clientId);
                  // Filter out the folder being moved
                  const filteredFolders = folders.filter(f => f.key !== contextMenu.file.key);
                  setAvailableFolders(filteredFolders);
                  
                  setShowMoveFileModal(true);
                  setContextMenu({ show: false, x: 0, y: 0, file: null });
                }}
                className="w-full px-4 py-2 text-left text-sm hover:bg-gray-100 flex items-center"
              >
                <Move className="h-4 w-4 mr-2 text-purple-600" />
                Move
              </button>
              <button
                onClick={() => {
                  setItemToDelete(contextMenu.file);
                  setShowDeleteConfirmModal(true);
                  setContextMenu({ show: false, x: 0, y: 0, file: null });
                }}
                className="w-full px-4 py-2 text-left text-sm hover:bg-gray-100 text-red-600 flex items-center"
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Delete
              </button>
            </>
          ) : (
            // File actions
            <>
              {contextMenu.file.hasMultipleVersions && (
                <button
                  onClick={() => {
                    const clientId = clientFolders.find(c => c.name === selectedPath[0])?.id;
                    if (clientId) {
                      fetchFileVersions(clientId, contextMenu.file.key, contextMenu.file.name);
                    }
                    setContextMenu({ show: false, x: 0, y: 0, file: null });
                  }}
                  className="w-full px-4 py-2 text-left text-sm hover:bg-gray-100 flex items-center border-b border-gray-200"
                >
                  <RefreshCw className="h-4 w-4 mr-2 text-theme-blue" />
                  <span className="flex-1">Versions</span>
                  <span className="text-xs bg-blue-100 text-blue-800 px-2 py-0.5 rounded-full">
                    {contextMenu.file.versionCount}
                  </span>
                </button>
              )}
              <button
                onClick={() => {
                  setFileToRename(contextMenu.file);
                  // Extract filename without extension
                  const fullName = contextMenu.file.original_name || contextMenu.file.name;
                  const nameWithoutExt = fullName.includes('.') ? fullName.substring(0, fullName.lastIndexOf('.')) : fullName;
                  setRenameFileValue(nameWithoutExt);
                  setShowRenameFileModal(true);
                  setContextMenu({ show: false, x: 0, y: 0, file: null });
                }}
                className="w-full px-4 py-2 text-left text-sm hover:bg-gray-100 flex items-center"
              >
                <File className="h-4 w-4 mr-2" />
                Rename
              </button>
              <button
                onClick={async () => {
                  setFileToMove(contextMenu.file);
                  setMoveDestinationPath([]);
                  setMoveDestinationKey('');
                  // Load current client's folders
                  const clientId = clientFolders.find(c => c.name === selectedPath[0])?.id;
                  if (clientId) {
                    const folders = await loadAvailableFolders(clientId);
                    setAvailableFolders(folders);
                  }
                  setShowMoveFileModal(true);
                  setContextMenu({ show: false, x: 0, y: 0, file: null });
                }}
                className="w-full px-4 py-2 text-left text-sm hover:bg-gray-100 flex items-center"
              >
                <Move className="h-4 w-4 mr-2" />
                Move
              </button>
              {isPreviewable(contextMenu.file.name) && (
                <button
                  onClick={() => {
                    openPreview(contextMenu.file);
                    setContextMenu({ show: false, x: 0, y: 0, file: null });
                  }}
                  className="w-full px-4 py-2 text-left text-sm hover:bg-gray-100 flex items-center"
                >
                  <Eye className="h-4 w-4 mr-2" />
                  Preview
                </button>
              )}
              <button
                onClick={() => {
                  copyUrlToClipboard(contextMenu.file.url);
                  setContextMenu({ show: false, x: 0, y: 0, file: null });
                }}
                className="w-full px-4 py-2 text-left text-sm hover:bg-gray-100 flex items-center"
              >
                <Copy className="h-4 w-4 mr-2" />
                Copy URL
              </button>
              <button
                onClick={() => {
                  window.open(contextMenu.file.url, '_blank');
                  setContextMenu({ show: false, x: 0, y: 0, file: null });
                }}
                className="w-full px-4 py-2 text-left text-sm hover:bg-gray-100 flex items-center"
              >
                <Download className="h-4 w-4 mr-2" />
                Download
              </button>
              <button
                onClick={() => {
                  setItemToDelete(contextMenu.file);
                  setShowDeleteConfirmModal(true);
                  setContextMenu({ show: false, x: 0, y: 0, file: null });
                }}
                className="w-full px-4 py-2 text-left text-sm hover:bg-gray-100 text-red-600 flex items-center"
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Delete
              </button>
            </>
          )}
        </div>
      )}

      {/* Preview Modal */}
      {showPreviewModal && previewData.type === 'pdf' && (
        <PDFViewer
          fileUrl={previewData.url}
          fileName={previewData.name}
          onClose={() => setShowPreviewModal(false)}
          token={token}
        />
      )}

      {showPreviewModal && previewData.type === 'office' && (
        <OfficeViewer
          fileUrl={previewData.url}
          fileName={previewData.name}
          onClose={() => setShowPreviewModal(false)}
          token={token}
        />
      )}

      {showPreviewModal && previewData.type === 'image' && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-6xl h-[95vh] flex flex-col">
            {/* Header */}
            <div className="flex justify-between items-center px-3 py-2 bg-gray-50">
              <div className="flex items-center space-x-3">
                <h3 className="text-sm font-semibold text-gray-900 truncate max-w-md">{previewData.name}</h3>
              </div>
              
              {/* Controls */}
              <div className="flex items-center space-x-1">
                <button
                  onClick={() => window.open(previewData.url, '_blank')}
                  className="p-1.5 rounded-md hover:bg-gray-200 transition-colors"
                  title="Download Image"
                >
                  <Download className="h-4 w-4" />
                </button>
                
                <button
                  onClick={() => setShowPreviewModal(false)}
                  className="p-1.5 rounded-md hover:bg-gray-200 transition-colors"
                  title="Close"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            </div>
            
            {/* Image Content */}
            <div className="flex-1 overflow-auto bg-gray-100 flex items-center justify-center p-4">
              <img 
                src={previewData.url} 
                alt={previewData.name}
                className="max-w-full max-h-full object-contain"
              />
            </div>

            {/* Footer */}
            <div className="flex justify-between items-center px-3 py-2 border-t border-gray-200 bg-gray-50">
              <div className="text-xs text-gray-600">
                {'\u00A0'}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add Customer Modal */}
      {showAddCustomerModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-md w-full max-h-[90vh] overflow-hidden shadow-2xl">
            {/* Sticky Header */}
            <div className="sticky top-0 z-10 px-6 py-4" style={{ background: 'linear-gradient(to right, var(--theme-primary), var(--theme-secondary))' }}>
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="bg-white bg-opacity-20 rounded-full p-2">
                    <Users className="h-5 w-5 text-white" />
                  </div>
                  <h3 className="text-lg font-semibold text-white">
                    Add New Customer
                  </h3>
                </div>
                <button
                  onClick={resetCustomerForm}
                  className="text-white hover:text-blue-100 bg-white bg-opacity-20 hover:bg-opacity-30 rounded-full p-2 transition-colors"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>

            {/* Scrollable Content */}
            <div className="px-6 py-4 max-h-[calc(90vh-140px)] overflow-y-auto">
              <form onSubmit={handleAddCustomer} className="space-y-4">
                {/* First Name and Last Name */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      First Name *
                    </label>
                    <input
                      type="text"
                      required
                      value={customerFormData.first_name}
                      onChange={(e) => setCustomerFormData({ ...customerFormData, first_name: e.target.value })}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none transition-colors focus:border-theme-blue focus:ring-2 focus:ring-blue-200"
                      placeholder="Enter first name"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Last Name *
                    </label>
                    <input
                      type="text"
                      required
                      value={customerFormData.last_name}
                      onChange={(e) => setCustomerFormData({ ...customerFormData, last_name: e.target.value })}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none transition-colors focus:border-theme-blue focus:ring-2 focus:ring-blue-200"
                      placeholder="Enter last name"
                    />
                  </div>
                </div>

                {/* Pet Names */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="block text-sm font-semibold text-gray-700">
                      Pet Names
                    </label>
                    <button
                      type="button"
                      onClick={addPet}
                      className="text-sm text-theme-blue hover:text-blue-800 flex items-center"
                    >
                      <Plus className="h-4 w-4 mr-1" />
                      Add Pet
                    </button>
                  </div>
                  
                  <div className="space-y-2">
                    {customerFormData.pets.map((pet, index) => (
                      <div key={index} className="flex items-center space-x-2">
                        <input
                          type="text"
                          value={pet.name}
                          onChange={(e) => updatePetName(index, e.target.value)}
                          className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:outline-none transition-colors focus:border-theme-blue focus:ring-2 focus:ring-blue-200"
                          placeholder="Enter pet name"
                        />
                        {customerFormData.pets.length > 1 && (
                          <button
                            type="button"
                            onClick={() => removePet(index)}
                            className="text-red-500 hover:text-red-700"
                          >
                            <X className="h-5 w-5" />
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                </div>

                {/* Phone */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Phone Number
                  </label>
                  <input
                    type="tel"
                    value={customerFormData.phone}
                    onChange={(e) => setCustomerFormData({ ...customerFormData, phone: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none transition-colors focus:border-theme-blue focus:ring-2 focus:ring-blue-200"
                    placeholder="(555) 123-4567"
                  />
                </div>

                {/* Email */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Email Address
                  </label>
                  <input
                    type="email"
                    value={customerFormData.email}
                    onChange={(e) => setCustomerFormData({ ...customerFormData, email: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none transition-colors focus:border-theme-blue focus:ring-2 focus:ring-blue-200"
                    placeholder="customer@example.com"
                  />
                </div>

                {/* SMS Opt-in */}
                <div className="flex items-center space-x-3">
                  <input
                    type="checkbox"
                    id="sms_opt_in"
                    checked={customerFormData.sms_opt_in}
                    onChange={(e) => setCustomerFormData({ ...customerFormData, sms_opt_in: e.target.checked })}
                    className="w-5 h-5 text-theme-blue border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                  />
                  <label htmlFor="sms_opt_in" className="text-sm text-gray-700">
                    SMS Opt-in
                  </label>
                </div>

                {/* Email Subscribed */}
                <div className="flex items-center space-x-3">
                  <input
                    type="checkbox"
                    id="email_subscribed"
                    checked={customerFormData.email_subscribed}
                    onChange={(e) => setCustomerFormData({ ...customerFormData, email_subscribed: e.target.checked })}
                    className="w-5 h-5 text-theme-blue border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                  />
                  <label htmlFor="email_subscribed" className="text-sm text-gray-700">
                    Email Subscribed
                  </label>
                </div>
              </form>
            </div>

            {/* Sticky Footer */}
            <div className="sticky bottom-0 px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-end space-x-3">
              <button
                onClick={resetCustomerForm}
                type="button"
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleAddCustomer}
                disabled={loading}
                className="px-4 py-2 text-white rounded-lg transition-colors disabled:opacity-50"
                style={{ background: 'linear-gradient(to right, var(--theme-primary), var(--theme-secondary))' }}
              >
                {loading ? 'Adding...' : 'Add Customer'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create Folder Modal */}
      {showCreateFolderModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-md w-full shadow-2xl">
            {/* Header */}
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="bg-green-100 rounded-full p-2">
                    <FolderPlus className="h-5 w-5 text-green-600" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900">
                    New Folder
                  </h3>
                </div>
                <button
                  onClick={() => {
                    setShowCreateFolderModal(false);
                    setNewFolderName('');
                  }}
                  className="text-gray-400 hover:text-gray-600 rounded-full p-2 hover:bg-gray-100 transition-colors"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="px-6 py-4">
              <div className="mb-4">
                <p className="text-sm text-gray-600 mb-2">
                  Creating folder in: <span className="font-medium">{getBreadcrumb()}</span>
                </p>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Folder Name
                </label>
                <input
                  type="text"
                  value={newFolderName}
                  onChange={(e) => setNewFolderName(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter' && newFolderName.trim()) {
                      handleCreateFolder();
                    }
                  }}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:border-green-500 focus:ring-2 focus:ring-green-200"
                  placeholder="Enter folder name"
                  autoFocus
                />
              </div>
            </div>

            {/* Footer */}
            <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowCreateFolderModal(false);
                  setNewFolderName('');
                }}
                type="button"
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateFolder}
                disabled={loading || !newFolderName.trim()}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
              >
                {loading ? 'Creating...' : 'Create Folder'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirmModal && itemToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-md w-full shadow-2xl">
            {/* Header */}
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex items-center space-x-3">
                <div className="bg-red-100 rounded-full p-2">
                  <Trash2 className="h-5 w-5 text-red-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900">
                  Delete {itemToDelete.type === 'folder' ? 'Folder' : 'File'}
                </h3>
              </div>
            </div>

            {/* Content */}
            <div className="px-6 py-4">
              <p className="text-sm text-gray-700">
                Are you sure you want to delete <span className="font-semibold">{itemToDelete.name}</span>?
                {itemToDelete.type === 'folder' && (
                  <span className="block mt-2 text-red-600">
                    Warning: This will delete the folder and all its contents.
                  </span>
                )}
                <span className="block mt-2 text-gray-600">
                  This action cannot be undone.
                </span>
              </p>
            </div>

            {/* Footer */}
            <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowDeleteConfirmModal(false);
                  setItemToDelete(null);
                }}
                type="button"
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteConfirm}
                disabled={loading}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 flex items-center space-x-2"
              >
                <Trash2 className="h-4 w-4" />
                <span>{loading ? 'Deleting...' : 'Delete'}</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Rename Folder Modal */}
      {showRenameFolderModal && folderToRename && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-md w-full shadow-2xl">
            {/* Header */}
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="bg-blue-100 rounded-full p-2">
                    <File className="h-5 w-5 text-theme-blue" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900">
                    Rename Folder
                  </h3>
                </div>
                <button
                  onClick={() => {
                    setShowRenameFolderModal(false);
                    setFolderToRename(null);
                    setRenameFolderValue('');
                  }}
                  className="text-gray-400 hover:text-gray-600 rounded-full p-2 hover:bg-gray-100 transition-colors"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="px-6 py-4">
              <div className="mb-4">
                <p className="text-sm text-gray-600 mb-2">
                  Current name: <span className="font-medium">{folderToRename.name}</span>
                </p>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  New Folder Name
                </label>
                <input
                  type="text"
                  value={renameFolderValue}
                  onChange={(e) => setRenameFolderValue(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter' && renameFolderValue.trim()) {
                      handleRenameFolder();
                    }
                  }}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:border-theme-blue focus:ring-2 focus:ring-blue-200"
                  placeholder="Enter new folder name"
                  autoFocus
                />
              </div>
            </div>

            {/* Footer */}
            <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowRenameFolderModal(false);
                  setFolderToRename(null);
                  setRenameFolderValue('');
                }}
                type="button"
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleRenameFolder}
                disabled={loading || !renameFolderValue.trim()}
                className="px-4 py-2 bg-theme-blue text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
              >
                {loading ? 'Renaming...' : 'Rename'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Rename File Modal */}
      {showRenameFileModal && fileToRename && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-md w-full shadow-2xl">
            {/* Header */}
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="bg-green-100 rounded-full p-2">
                    <File className="h-5 w-5 text-green-600" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900">
                    Rename File
                  </h3>
                </div>
                <button
                  onClick={() => {
                    setShowRenameFileModal(false);
                    setFileToRename(null);
                    setRenameFileValue('');
                  }}
                  className="text-gray-400 hover:text-gray-600 rounded-full p-2 hover:bg-gray-100 transition-colors"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="px-6 py-4">
              <div className="mb-4">
                <p className="text-sm text-gray-600 mb-2">
                  Current name: <span className="font-medium">{fileToRename.original_name || fileToRename.name}</span>
                </p>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  New File Name
                </label>
                <div className="flex items-center space-x-2">
                  <input
                    type="text"
                    value={renameFileValue}
                    onChange={(e) => setRenameFileValue(e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter' && renameFileValue.trim()) {
                        handleRenameFile();
                      }
                    }}
                    className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:border-green-500 focus:ring-2 focus:ring-green-200"
                    placeholder="Enter file name"
                    autoFocus
                  />
                  {fileToRename && (fileToRename.original_name || fileToRename.name).includes('.') && (
                    <span className="text-sm font-medium text-gray-600 bg-gray-100 px-3 py-3 rounded-lg border border-gray-300">
                      {(fileToRename.original_name || fileToRename.name).substring((fileToRename.original_name || fileToRename.name).lastIndexOf('.'))}
                    </span>
                  )}
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  File extension is preserved automatically
                </p>
              </div>
            </div>

            {/* Footer */}
            <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowRenameFileModal(false);
                  setFileToRename(null);
                  setRenameFileValue('');
                }}
                type="button"
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleRenameFile}
                disabled={loading || !renameFileValue.trim()}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
              >
                {loading ? 'Renaming...' : 'Rename'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Recycle Bin Modal */}
      {showRecycleBin && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-hidden shadow-2xl">
            {/* Header */}
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="bg-gray-100 rounded-full p-2">
                    <Trash2 className="h-5 w-5 text-gray-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">
                      Recycle Bin - {selectedPath[0]}
                    </h3>
                    {recycleBinBrowseFolder && (
                      <p className="text-sm text-gray-500">
                        Viewing: {recycleBinBrowseFolder}
                        {recycleBinBrowsePath.length > 0 && ` / ${recycleBinBrowsePath.join(' / ')}`}
                      </p>
                    )}
                  </div>
                </div>
                <button
                  onClick={() => {
                    setShowRecycleBin(false);
                    setRecycleBinBrowseFolder(null);
                    setRecycleBinBrowsePath([]);
                  }}
                  className="text-gray-400 hover:text-gray-600 rounded-full p-2 hover:bg-gray-100 transition-colors"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
              {/* Back button when browsing folder */}
              {recycleBinBrowseFolder && (
                <div className="mt-3">
                  <button
                    onClick={() => {
                      const clientId = clientFolders.find(c => c.name === selectedPath[0])?.id;
                      if (clientId) {
                        if (recycleBinBrowsePath.length > 0) {
                          // Go back one level
                          const newPath = recycleBinBrowsePath.slice(0, -1);
                          setRecycleBinBrowsePath(newPath);
                          browseDeletingFolder(clientId, recycleBinBrowseFolder, newPath.join('/'));
                        } else {
                          // Go back to main recycle bin
                          setRecycleBinBrowseFolder(null);
                          setRecycleBinBrowsePath([]);
                          fetchRecycleBin(clientId);
                        }
                      }
                    }}
                    className="text-sm text-theme-blue hover:text-blue-700 flex items-center space-x-1"
                  >
                    <ChevronDown className="h-4 w-4 transform rotate-90" />
                    <span>Back {recycleBinBrowsePath.length > 0 ? 'to Parent Folder' : 'to Recycle Bin'}</span>
                  </button>
                </div>
              )}
            </div>

            {/* Content */}
            <div className="px-6 py-4 max-h-[calc(90vh-140px)] overflow-y-auto">
              {loading ? (
                <div className="flex items-center justify-center py-8">
                  <RefreshCw className="h-6 w-6 animate-spin text-gray-400" />
                  <span className="ml-2 text-gray-600">Loading recycle bin...</span>
                </div>
              ) : recycleBinItems.length === 0 ? (
                <div className="text-center py-12">
                  <Trash2 className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600 text-lg">Recycle bin is empty</p>
                  <p className="text-gray-500 text-sm mt-2">Deleted items will appear here</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {recycleBinItems.map((item) => (
                    <div
                      key={item.key || item.name}
                      className={`flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:bg-gray-50 ${
                        item.type === 'folder' ? 'cursor-pointer' : ''
                      }`}
                      onClick={() => {
                        if (item.type === 'folder') {
                          const clientId = clientFolders.find(c => c.name === selectedPath[0])?.id;
                          if (clientId) {
                            if (!recycleBinBrowseFolder) {
                              // Main view - browse into deleted folder
                              browseDeletingFolder(clientId, item.name);
                            } else {
                              // Browsing view - go deeper into subfolder
                              const newPath = [...recycleBinBrowsePath, item.name];
                              setRecycleBinBrowsePath(newPath);
                              browseDeletingFolder(clientId, recycleBinBrowseFolder, newPath.join('/'));
                            }
                          }
                        }
                      }}
                    >
                      <div className="flex items-center space-x-3">
                        <div className="text-2xl">
                          {item.type === 'folder' ? (
                            <Folder className="h-6 w-6 text-theme-blue" />
                          ) : (
                            getFileIcon(item.name)
                          )}
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">
                            {item.name}
                            {item.item_count > 0 && (
                              <span className="ml-2 text-xs text-gray-500">
                                ({item.item_count} items)
                              </span>
                            )}
                          </p>
                          <p className="text-sm text-gray-500">
                            Deleted: {item.deleted_at ? new Date(item.deleted_at).toLocaleDateString() : 'Unknown'}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        {/* Only show restore for files OR top-level folders (not subfolders) */}
                        {(item.type !== 'folder' || !recycleBinBrowseFolder) && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleRestoreItem(item);
                            }}
                            disabled={loading}
                            className="px-3 py-1.5 text-sm bg-green-600 text-white rounded hover:bg-green-700 transition-colors disabled:opacity-50 flex items-center space-x-1"
                          >
                            <RotateCcw className="h-3.5 w-3.5" />
                            <span>Restore</span>
                          </button>
                        )}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handlePermanentDelete(item);
                          }}
                          disabled={loading}
                          className="px-3 py-1.5 text-sm bg-red-600 text-white rounded hover:bg-red-700 transition-colors disabled:opacity-50 flex items-center space-x-1"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                          <span>Delete Forever</span>
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-end">
              <button
                onClick={() => {
                  setShowRecycleBin(false);
                  setRecycleBinBrowseFolder(null);
                  setRecycleBinBrowsePath([]);
                }}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Global Recycle Bin Modal */}
      {showGlobalRecycleBin && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-5xl w-full max-h-[90vh] overflow-hidden shadow-2xl">
            {/* Header */}
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="bg-gray-100 rounded-full p-2">
                    <Trash2 className="h-5 w-5 text-gray-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">
                      Recycle Bin - All Clients
                    </h3>
                    {globalRecycleBinBrowseFolder ? (
                      <p className="text-sm text-gray-600">
                        Viewing: {globalRecycleBinBrowseFolder}
                        {globalRecycleBinBrowsePath.length > 0 && ` / ${globalRecycleBinBrowsePath.join(' / ')}`}
                      </p>
                    ) : (
                      <p className="text-sm text-gray-600">
                        {globalRecycleBinItems.length} item{globalRecycleBinItems.length !== 1 ? 's' : ''} in recycle bin
                      </p>
                    )}
                  </div>
                </div>
                <button
                  onClick={() => {
                    setShowGlobalRecycleBin(false);
                    setGlobalRecycleBinBrowseFolder(null);
                    setGlobalRecycleBinBrowseClient(null);
                    setGlobalRecycleBinBrowsePath([]);
                  }}
                  className="text-gray-400 hover:text-gray-600 rounded-full p-2 hover:bg-gray-100 transition-colors"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
              {/* Back button when browsing folder */}
              {globalRecycleBinBrowseFolder && (
                <div className="mt-3">
                  <button
                    onClick={() => {
                      if (globalRecycleBinBrowsePath.length > 0) {
                        // Go back one level
                        const newPath = globalRecycleBinBrowsePath.slice(0, -1);
                        setGlobalRecycleBinBrowsePath(newPath);
                        browseGlobalDeletedFolder(
                          globalRecycleBinBrowseClient,
                          globalRecycleBinBrowseFolder,
                          newPath.join('/')
                        );
                      } else {
                        // Go back to main recycle bin
                        setGlobalRecycleBinBrowseFolder(null);
                        setGlobalRecycleBinBrowseClient(null);
                        setGlobalRecycleBinBrowsePath([]);
                        fetchGlobalRecycleBin();
                      }
                    }}
                    className="text-sm text-theme-blue hover:text-blue-700 flex items-center space-x-1"
                  >
                    <ChevronDown className="h-4 w-4 transform rotate-90" />
                    <span>Back {globalRecycleBinBrowsePath.length > 0 ? 'to Parent Folder' : 'to Recycle Bin'}</span>
                  </button>
                </div>
              )}
            </div>

            {/* Content */}
            <div className="px-6 py-4 max-h-[calc(90vh-140px)] overflow-y-auto">
              {loading ? (
                <div className="flex items-center justify-center py-8">
                  <RefreshCw className="h-6 w-6 animate-spin text-gray-400" />
                  <span className="ml-2 text-gray-600">Loading recycle bin...</span>
                </div>
              ) : globalRecycleBinItems.length === 0 ? (
                <div className="text-center py-12">
                  <Trash2 className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600 text-lg">Recycle bin is empty</p>
                  <p className="text-gray-500 text-sm mt-2">Deleted items from all clients will appear here</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {globalRecycleBinItems.map((item) => (
                    <div
                      key={item.key || item.name}
                      className={`flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:bg-gray-50 ${
                        item.type === 'folder' ? 'cursor-pointer' : ''
                      }`}
                      onClick={() => {
                        if (item.type === 'folder') {
                          if (!globalRecycleBinBrowseFolder && item.client_id) {
                            // Main view - browse into deleted folder
                            browseGlobalDeletedFolder(item.client_id, item.name);
                          } else if (globalRecycleBinBrowseFolder) {
                            // Browsing view - go deeper into subfolder
                            const newPath = [...globalRecycleBinBrowsePath, item.name];
                            setGlobalRecycleBinBrowsePath(newPath);
                            browseGlobalDeletedFolder(
                              globalRecycleBinBrowseClient,
                              globalRecycleBinBrowseFolder,
                              newPath.join('/')
                            );
                          }
                        }
                      }}
                    >
                      <div className="flex items-center space-x-3 flex-1">
                        <div>
                          {item.type === 'folder' ? (
                            <Folder className="h-6 w-6 text-theme-blue" />
                          ) : (
                            getFileIcon(item.name)
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-gray-900 truncate">
                            {item.name}
                            {item.item_count > 0 && (
                              <span className="ml-2 text-xs text-gray-500">
                                ({item.item_count} items)
                              </span>
                            )}
                          </p>
                          <div className="flex items-center space-x-4 text-xs text-gray-500 mt-1">
                            <span className="flex items-center space-x-1">
                              <Users className="h-3 w-3" />
                              <span>{item.client_name}</span>
                            </span>
                            <span>
                              Deleted: {item.deleted_at ? new Date(item.deleted_at).toLocaleDateString() : 'Unknown'}
                            </span>
                            {item.deleted_by && (
                              <span className="text-gray-400">
                                by {item.deleted_by}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2 ml-4">
                        {/* Only show restore for files OR top-level folders (not subfolders) */}
                        {(item.type !== 'folder' || !globalRecycleBinBrowseFolder) && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleRestoreGlobalItem(item);
                            }}
                            disabled={loading}
                            className="px-3 py-1.5 text-sm bg-green-600 text-white rounded hover:bg-green-700 transition-colors disabled:opacity-50 flex items-center space-x-1"
                          >
                            <RotateCcw className="h-3.5 w-3.5" />
                            <span>Restore</span>
                          </button>
                        )}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handlePermanentDeleteGlobal(item);
                          }}
                          disabled={loading}
                          className="px-3 py-1.5 text-sm bg-red-600 text-white rounded hover:bg-red-700 transition-colors disabled:opacity-50 flex items-center space-x-1"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                          <span>Delete Forever</span>
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-between items-center">
              <p className="text-sm text-gray-600">
                Items are automatically deleted after the configured retention period
              </p>
              <button
                onClick={() => setShowGlobalRecycleBin(false)}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Move File Modal */}
      {showMoveFileModal && fileToMove && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-2xl w-full shadow-2xl max-h-[80vh] flex flex-col">
            {/* Header - Sticky */}
            <div className="px-6 py-4 border-b border-gray-200 flex-shrink-0">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="bg-blue-100 rounded-full p-2">
                    <Move className="h-5 w-5 text-theme-blue" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">
                      Move File
                    </h3>
                    <p className="text-sm text-gray-500 mt-1">
                      Moving: {fileToMove.original_name || fileToMove.name}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => {
                    setShowMoveFileModal(false);
                    setFileToMove(null);
                    setMoveDestinationPath([]);
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>

            {/* Content - Scrollable Folder Navigator */}
            <div className="flex-1 overflow-y-auto px-6 py-4">
              <p className="text-sm text-gray-600 mb-4">
                Select a destination folder:
              </p>
              
              {/* Breadcrumb showing current selection */}
              {moveDestinationPath.length > 0 && (
                <div className="mb-4 p-3 bg-blue-50 rounded-lg flex items-center justify-between">
                  <div className="flex items-center">
                    <Folder className="h-4 w-4 text-theme-blue mr-2" />
                    <span className="text-sm text-blue-800 font-medium">
                      {moveDestinationPath.join(' / ')}
                    </span>
                  </div>
                  <span className="text-xs text-theme-blue">Selected</span>
                </div>
              )}

              {/* Folder List */}
              <div className="space-y-2">
                {availableFolders.map((folder) => (
                  <button
                    key={folder.key || folder.name}
                    onClick={async () => {
                      // Get clientId from fileToMove if it's a folder, otherwise from selectedPath
                      const clientId = fileToMove?.clientId || clientFolders.find(c => c.name === selectedPath[0])?.id;
                      if (!clientId) return;
                      
                      // Store the selected folder key
                      const newPath = moveDestinationPath.length > 0 
                        ? [...moveDestinationPath, folder.name]
                        : [folder.name];
                      
                      // Use folder.key which includes the bucket prefix
                      setMoveDestinationKey(folder.key || `Client Records/${clientId}/${newPath.join('/')}/`);
                      setMoveDestinationPath(newPath);
                      
                      // Load subfolders of the selected folder
                      const subfolders = await loadAvailableFolders(clientId, newPath);
                      setAvailableFolders(subfolders);
                    }}
                    className="w-full flex items-center space-x-3 p-3 border border-gray-200 rounded-lg hover:bg-gray-50 hover:border-blue-300 transition-all text-left"
                  >
                    <Folder className="h-5 w-5 text-theme-blue flex-shrink-0" />
                    <span className="text-sm font-medium text-gray-900 truncate flex-1">
                      {folder.name}
                    </span>
                    <ChevronRight className="h-4 w-4 text-gray-400 flex-shrink-0" />
                  </button>
                ))}
                
                {availableFolders.length === 0 && moveDestinationPath.length > 0 && (
                  <div className="text-center text-gray-500 text-sm py-8 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
                    <Folder className="h-12 w-12 text-gray-400 mx-auto mb-2" />
                    <p>No subfolders in this folder</p>
                    <p className="text-xs mt-1">Click "Move Here" to move the file to this location</p>
                  </div>
                )}
                
                {availableFolders.length === 0 && moveDestinationPath.length === 0 && (
                  <p className="text-center text-gray-500 text-sm py-8">
                    No folders available
                  </p>
                )}
              </div>
            </div>

            {/* Footer - Sticky */}
            <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-between flex-shrink-0">
              <button
                onClick={async () => {
                  const clientId = clientFolders.find(c => c.name === selectedPath[0])?.id;
                  if (!clientId) return;
                  
                  if (moveDestinationPath.length > 1) {
                    // Go back one level
                    const newPath = moveDestinationPath.slice(0, -1);
                    setMoveDestinationPath(newPath);
                    const subfolders = await loadAvailableFolders(clientId, newPath);
                    setAvailableFolders(subfolders);
                  } else if (moveDestinationPath.length === 1) {
                    // Go back to root folders
                    setMoveDestinationPath([]);
                    const rootFolders = await loadAvailableFolders(clientId);
                    setAvailableFolders(rootFolders);
                  }
                }}
                disabled={moveDestinationPath.length === 0}
                className="px-4 py-2 text-gray-700 hover:bg-gray-200 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                ← Back
              </button>
              <div className="flex space-x-3">
                <button
                  onClick={() => {
                    setShowMoveFileModal(false);
                    setFileToMove(null);
                    setMoveDestinationPath([]);
                    setMoveDestinationKey('');
                  }}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    if (moveDestinationKey && fileToMove) {
                      // Use the same approach as Hospital Records - handleMoveFile handles both files and folders
                      handleMoveFile(fileToMove, moveDestinationKey);
                    }
                  }}
                  disabled={!moveDestinationKey}
                  className="px-4 py-2 bg-theme-blue text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Move Here
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Versions Modal */}
      {showVersionsModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-3xl w-full max-h-[80vh] overflow-hidden shadow-2xl">
            {/* Header */}
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="bg-blue-100 rounded-full p-2">
                    <RefreshCw className="h-5 w-5 text-theme-blue" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">
                      Version History
                    </h3>
                    <p className="text-sm text-gray-600">{selectedFileName}</p>
                  </div>
                </div>
                <button
                  onClick={() => setShowVersionsModal(false)}
                  className="text-gray-400 hover:text-gray-600 rounded-full p-2 hover:bg-gray-100 transition-colors"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="px-6 py-4 max-h-[calc(80vh-140px)] overflow-y-auto">
              {loading ? (
                <div className="flex items-center justify-center py-8">
                  <RefreshCw className="h-6 w-6 animate-spin text-gray-400" />
                  <span className="ml-2 text-gray-600">Loading versions...</span>
                </div>
              ) : selectedFileVersions.length === 0 ? (
                <div className="text-center py-8">
                  <p className="text-gray-600">No versions found</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {selectedFileVersions.map((version, index) => (
                    <div
                      key={version.key}
                      className={`flex items-center justify-between p-4 border rounded-lg ${
                        index === 0 ? 'border-theme-blue bg-blue-50' : 'border-gray-200 hover:bg-gray-50'
                      }`}
                    >
                      <div className="flex items-center space-x-4 flex-1">
                        <div>
                          <div className="flex items-center space-x-2">
                            <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-semibold ${
                              index === 0 ? 'bg-theme-blue text-white' : 'bg-gray-200 text-gray-700'
                            }`}>
                              Version {version.version}
                            </span>
                            {index === 0 && (
                              <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded-full font-medium">
                                Current
                              </span>
                            )}
                          </div>
                          <div className="mt-2 text-sm text-gray-600">
                            <div>Uploaded: {new Date(version.uploaded_at).toLocaleString()}</div>
                            {version.uploaded_by && (
                              <div>By: {version.uploaded_by}</div>
                            )}
                            <div className="text-xs text-gray-500">
                              Size: {(version.size / 1024).toFixed(2)} KB
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => {
                            setPreviewData({
                              name: selectedFileName,
                              url: version.url,
                              type: getFileType(selectedFileName)
                            });
                            setShowPreviewModal(true);
                          }}
                          className="px-3 py-1.5 text-sm bg-gray-100 text-gray-700 rounded hover:bg-gray-200 transition-colors flex items-center space-x-1"
                        >
                          <Eye className="h-3.5 w-3.5" />
                          <span>Preview</span>
                        </button>
                        <button
                          onClick={() => window.open(version.url, '_blank')}
                          className="px-3 py-1.5 text-sm bg-theme-blue text-white rounded hover:bg-blue-700 transition-colors flex items-center space-x-1"
                        >
                          <Download className="h-3.5 w-3.5" />
                          <span>Download</span>
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-end">
              <button
                onClick={() => setShowVersionsModal(false)}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Restore Confirmation Modal */}
      {showRestoreConfirmModal && itemToRestore && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-md w-full shadow-2xl">
            {/* Header */}
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex items-center space-x-3">
                <div className="bg-green-100 rounded-full p-2">
                  <RotateCcw className="h-5 w-5 text-green-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900">
                  Restore {itemToRestore.type === 'folder' ? 'Folder' : 'File'}
                </h3>
              </div>
            </div>

            {/* Content */}
            <div className="px-6 py-4">
              <p className="text-gray-700 mb-4">
                Are you sure you want to restore this {itemToRestore.type === 'folder' ? 'folder' : 'file'}?
              </p>
              
              <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                <div className="flex items-center space-x-2">
                  {itemToRestore.type === 'folder' ? (
                    <Folder className="h-5 w-5 text-theme-blue flex-shrink-0" />
                  ) : (
                    <File className="h-5 w-5 text-gray-500 flex-shrink-0" />
                  )}
                  <p className="font-medium text-gray-900 break-all">
                    {itemToRestore.name}
                  </p>
                </div>
                
                {itemToRestore.item_count > 0 && (
                  <p className="text-sm text-gray-600 ml-7">
                    Contains {itemToRestore.item_count} {itemToRestore.item_count === 1 ? 'item' : 'items'}
                  </p>
                )}
                
                {isGlobalRestore && itemToRestore.client_name && (
                  <p className="text-sm text-gray-600 ml-7">
                    Client: {itemToRestore.client_name}
                  </p>
                )}
                
                {itemToRestore.deleted_at && (
                  <p className="text-xs text-gray-500 ml-7">
                    Deleted: {new Date(itemToRestore.deleted_at).toLocaleString()}
                  </p>
                )}
              </div>
              
              <p className="text-sm text-gray-600 mt-4">
                The {itemToRestore.type === 'folder' ? 'folder and all its contents will' : 'file will'} be restored to {itemToRestore.type === 'folder' ? 'its' : 'its'} original location.
              </p>
            </div>

            {/* Footer */}
            <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowRestoreConfirmModal(false);
                  setItemToRestore(null);
                }}
                disabled={loading}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={confirmRestore}
                disabled={loading}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 flex items-center space-x-2"
              >
                {loading ? (
                  <>
                    <RefreshCw className="h-4 w-4 animate-spin" />
                    <span>Restoring...</span>
                  </>
                ) : (
                  <>
                    <RotateCcw className="h-4 w-4" />
                    <span>Restore</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}


      {/* Tree Context Menu */}
      {treeContextMenu.show && (
        <>
          <div 
            className="fixed inset-0 z-40" 
            onClick={() => setTreeContextMenu({ show: false, x: 0, y: 0, folder: null, clientId: null, folderPath: '' })}
          />
          <div
            className="fixed bg-white rounded-lg shadow-xl border border-gray-200 py-1 z-50 min-w-[180px]"
            style={{
              left: `${treeContextMenu.x}px`,
              top: `${treeContextMenu.y}px`
            }}
          >
            {/* New Folder */}
            <button
              onClick={() => {
                setCreateSubfolderParent({
                  clientId: treeContextMenu.clientId,
                  folderPath: treeContextMenu.folderPath,
                  clientName: treeContextMenu.clientName
                });
                setShowCreateSubfolderModal(true);
                setTreeContextMenu({ show: false, x: 0, y: 0, folder: null, clientId: null, folderPath: '' });
              }}
              className="w-full px-4 py-2 text-left text-sm hover:bg-gray-100 flex items-center space-x-2"
            >
              <FolderPlus className="h-4 w-4 text-theme-blue" />
              <span>New Folder</span>
            </button>
            
            {/* Rename */}
            <button
              onClick={() => {
                // Add clientId to the folder object for rename handler
                const folderWithClient = {
                  ...treeContextMenu.folder,
                  clientId: treeContextMenu.clientId
                };
                setFolderToRename(folderWithClient);
                setRenameFolderValue(treeContextMenu.folder.name);
                setShowRenameFolderModal(true);
                setTreeContextMenu({ show: false, x: 0, y: 0, folder: null, clientId: null, folderPath: '' });
              }}
              className="w-full px-4 py-2 text-left text-sm hover:bg-gray-100 flex items-center space-x-2"
            >
              <Edit2 className="h-4 w-4 text-gray-600" />
              <span>Rename</span>
            </button>
            
            {/* Move */}
            <button
              onClick={async () => {
                // Set up folder to move
                const folderToMove = {
                  name: treeContextMenu.folder.name,
                  type: 'folder',
                  clientId: treeContextMenu.clientId,
                  folderPath: treeContextMenu.folderPath,
                  clientName: treeContextMenu.clientName
                };
                setFileToMove(folderToMove);
                
                // Fetch all folders in this client for destination selection
                const clientId = treeContextMenu.clientId;
                try {
                  const response = await fetch(`${API_BASE_URL}/api/records/clients/${clientId}/files`, {
                    headers: {
                      'Authorization': `Bearer ${token}`
                    }
                  });
                  if (response.ok) {
                    const data = await response.json();
                    // Filter out the folder being moved
                    const filteredFolders = (data.folders || []).filter(f => f.name !== folderToMove.name);
                    setAvailableFolders(filteredFolders);
                  }
                } catch (error) {
                  console.error('Error fetching folders:', error);
                }
                
                setMoveDestinationPath([]);
                setMoveDestinationKey('');
                setShowMoveFileModal(true);
                setTreeContextMenu({ show: false, x: 0, y: 0, folder: null, clientId: null, folderPath: '' });
              }}
              className="w-full px-4 py-2 text-left text-sm hover:bg-gray-100 flex items-center space-x-2"
            >
              <Move className="h-4 w-4 text-gray-600" />
              <span>Move</span>
            </button>
            
            {/* Delete */}
            <button
              onClick={() => {
                // Add clientId to the folder object for delete handler
                const folderWithClient = {
                  ...treeContextMenu.folder,
                  clientId: treeContextMenu.clientId
                };
                setItemToDelete(folderWithClient);
                setShowDeleteConfirmModal(true);
                setTreeContextMenu({ show: false, x: 0, y: 0, folder: null, clientId: null, folderPath: '' });
              }}
              className="w-full px-4 py-2 text-left text-sm hover:bg-gray-100 text-red-600 flex items-center space-x-2"
            >
              <Trash2 className="h-4 w-4" />
              <span>Delete</span>
            </button>
          </div>
        </>
      )}

      {/* Create Subfolder Modal */}
      {showCreateSubfolderModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-md w-full shadow-2xl">
            {/* Header */}
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex items-center space-x-3">
                <div className="bg-blue-100 rounded-full p-2">
                  <FolderPlus className="h-5 w-5 text-theme-blue" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900">
                  New Folder
                </h3>
              </div>
            </div>

            {/* Content */}
            <div className="px-6 py-4">
              <p className="text-sm text-gray-600 mb-4">
                Create a new subfolder inside: <span className="font-medium">{createSubfolderParent.folderPath || 'Root'}</span>
              </p>
              
              <input
                type="text"
                value={newSubfolderName}
                onChange={(e) => setNewSubfolderName(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    handleCreateSubfolder();
                  }
                }}
                placeholder="Folder name"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                autoFocus
              />
            </div>

            {/* Actions */}
            <div className="px-6 py-4 bg-gray-50 rounded-b-xl flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowCreateSubfolderModal(false);
                  setNewSubfolderName('');
                }}
                className="px-4 py-2 text-gray-700 hover:bg-gray-200 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateSubfolder}
                disabled={!newSubfolderName.trim()}
                className="px-4 py-2 bg-theme-blue text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
              >
                <FolderPlus className="h-4 w-4" />


      {/* Client Context Menu (only New Folder) */}
      {clientContextMenu.show && (
        <>
          <div 
            className="fixed inset-0 z-40" 
            onClick={() => setClientContextMenu({ show: false, x: 0, y: 0, client: null })}
          />
          <div
            className="fixed bg-white rounded-lg shadow-xl border border-gray-200 py-1 z-50 min-w-[160px]"
            style={{
              left: `${clientContextMenu.x}px`,
              top: `${clientContextMenu.y}px`
            }}
          >
            {/* New Folder */}
            <button
              onClick={() => {
                setCreateSubfolderParent({
                  clientId: clientContextMenu.client.id,
                  folderPath: '',
                  clientName: clientContextMenu.client.name
                });
                setShowCreateSubfolderModal(true);
                setClientContextMenu({ show: false, x: 0, y: 0, client: null });
              }}
              className="w-full px-4 py-2 text-left text-sm hover:bg-gray-100 flex items-center space-x-2"
            >
              <FolderPlus className="h-4 w-4 text-theme-blue" />
              <span>New Folder</span>
            </button>
          </div>
        </>
      )}

                <span>Create</span>
              </button>
            </div>
          </div>
        </div>
      )}


      {/* Notification Toast */}
      {notification.show && (
        <div className="fixed top-4 right-4 z-[9999] animate-slide-in-right">
          <div className={`flex items-center space-x-3 px-4 py-3 rounded-lg shadow-lg ${
            notification.type === 'success' 
              ? 'bg-green-50 border border-green-200' 
              : 'bg-red-50 border border-red-200'
          }`}>
            <div className={`flex-shrink-0 ${
              notification.type === 'success' ? 'text-green-600' : 'text-red-600'
            }`}>
              {notification.type === 'success' ? (
                <Check className="h-5 w-5" />
              ) : (
                <AlertCircle className="h-5 w-5" />
              )}
            </div>
            <p className={`text-sm font-medium ${
              notification.type === 'success' ? 'text-green-800' : 'text-red-800'
            }`}>
              {notification.message}
            </p>
            <button
              onClick={() => setNotification({ show: false, message: '', type: 'success' })}
              className={`flex-shrink-0 ml-2 ${
                notification.type === 'success' ? 'text-green-600 hover:text-green-800' : 'text-red-600 hover:text-red-800'
              }`}
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClientRecords;
